import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import api from "services/api";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  ListItemIcon,
  Typography,
  Stack,
  Button,
  ListItemText,
  Divider,
  Popover,
  Popper,
  Paper,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  ListSubheader,
} from "@mui/material";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import {
  GridFooter,
  GridFooterContainer,
  GridToolbarContainer,
  gridClasses,
  useGridApiRef,
} from "@mui/x-data-grid";
import { loadExcelJS } from "helpers/loadExcelJS";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import Grid from "@mui/material/Grid2";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import dayjs from "dayjs";
import CustomDateSelector from "components/CustomDateSelector";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import { CONST } from "constants/CONST";
import CEQuickFilter from "components/CEQuickFilter";
import DataGridResponsive from "components/DataGridResponsive";
import QuickFilterField from "components/QuickFilterField";
import { usePageTitle } from "hooks/usePageTitle";
import {
  buildEmailThreadLink,
  getDeadlineProximityColor,
  getStatusColor,
  normalizePhoneNumber,
  numericComparator,
  parseEmailThreadIds,
} from "helpers/ServiceHelpers";
import {
  checkAppliesToCategory,
  getWorstCheckSeverity,
  loadValidationCheckConfig,
  runServiceChecks,
} from "helpers/serviceValidationChecks";
import ServiceChecksIndicator from "components/ServiceChecksIndicator";
import ChecksSeverityBadge from "components/ChecksSeverityBadge";
import CommentRoundedIcon from "@mui/icons-material/CommentRounded";
// 2026-10-12 — mini profile side panel icons (same set as Interpretations)
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import ContactPhoneRoundedIcon from "@mui/icons-material/ContactPhoneRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import SmsRoundedIcon from "@mui/icons-material/SmsRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
// 2026-10-13 — side-panel ⋮ menu mirrors the profile Actions panel icons.
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CloudIcon from "@mui/icons-material/Cloud";
import { NAVBAR_HEIGHT } from "constants/layout";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";

// Batch C wave 4 — Translations columns whose desktop header uses
// ColumnDateFilter (date-range filter) instead of ColumnFilter. Used to build
// the card-mode filter-sheet entries with identical semantics.
const CARD_MODE_DATE_FILTER_FIELDS = [
  "Date",
  "Deadline",
  "DeliveryDate",
  "CheckedOn",
];

// ── 2026-10-30 — Deadline-alert deep-link filter ─────────────────────────────
// The "X past due" / "X not completed" pills link here as
// /translations?ids=55,56&months=2026-02,2026-05,2026-08,2026-10. `ids` is a
// comma-separated list of service ids, `months` a comma-separated list of
// YYYY-MM values. Both are TRANSIENT: they drive the visible range plus an
// extra row predicate and are never written to localStorage (the saved
// month/year preference and the saved column filters must survive the visit).
// Malformed/absent values parse to null so the screen behaves as before.
const URL_MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const parseUrlIdList = (raw) => {
  if (!raw) return null;
  const ids = String(raw)
    .split(",")
    .map((value) => Number(String(value).trim()))
    .filter((value) => Number.isFinite(value));
  const uniqueIds = Array.from(new Set(ids));
  return uniqueIds.length > 0 ? uniqueIds : null;
};

const parseUrlMonthRange = (raw) => {
  if (!raw) return null;
  const entries = String(raw)
    .split(",")
    .map((value) => String(value).trim())
    .map((value) => {
      const match = /^(\d{4})-(\d{1,2})$/.exec(value);
      if (!match) return null;
      const monthIndex = Number(match[2]) - 1;
      if (monthIndex < 0 || monthIndex > 11) return null;
      return { year: match[1], month: URL_MONTH_LABELS[monthIndex] };
    })
    .filter(Boolean);

  if (entries.length === 0) return null;

  // The year selector is single-select, so a range spanning two years keeps
  // the most recent one — and only that year's months.
  const year = entries.reduce(
    (latest, entry) => (entry.year > latest ? entry.year : latest),
    entries[0].year,
  );
  const monthsForYear = Array.from(
    new Set(
      entries
        .filter((entry) => entry.year === year)
        .map((entry) => entry.month),
    ),
  );

  return { year, months: monthsForYear };
};

// ── 2026-10-12 (Amine) — Mini profile side panel building blocks ────────────
// Ported from the Interpretations list (same architecture and styling) so
// both service lists behave identically. Width constraints + persistence for
// the resizable docked panel (own storage key — the two panels do not share
// a width).
const SIDE_PANEL_MIN_WIDTH = 320;
const SIDE_PANEL_MAX_WIDTH = 760;
const SIDE_PANEL_WIDTH_STORAGE_KEY = "TRANSLATIONS_SIDE_PANEL_WIDTH";
const clampSidePanelWidth = (width) =>
  Math.min(SIDE_PANEL_MAX_WIDTH, Math.max(SIDE_PANEL_MIN_WIDTH, width));

// Sequelize TINYINT/BOOLEAN can arrive as false/0/"0"/"false" — the engine
// overlay treats ONLY these as disabled (undefined reads as enabled, which
// matches the seeded rows), so the empty-state oracle mirrors it exactly.
const isCheckDisabledFlag = (value) =>
  value === false || value === 0 || value === "0" || value === "false";

// Compact section header (tinted icon bubble + title) + key/value rows for
// the desktop single-click panel.
const SidePanelSection = ({ title, icon, children }) => (
  <Box sx={{ px: 2, py: 1.25 }}>
    <Stack direction="row" spacing={1} alignItems="center">
      {icon && (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "primary.main",
            bgcolor: (theme) => `${theme.palette.primary.main}14`,
          }}
        >
          {icon}
        </Box>
      )}
      <Typography
        sx={{
          color: "text.primary",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontWeight: 700,
          fontSize: "0.95rem",
        }}
      >
        {title}
      </Typography>
    </Stack>
    <Box sx={{ mt: 1 }}>{children}</Box>
  </Box>
);

// Inline quick-action buttons for side panel rows: small muted icon buttons
// (call / text / email / open profile). Callers pass [] when the data is
// missing so no dead buttons ever render.
const SidePanelActions = ({ actions }) => {
  if (!actions || actions.length === 0) return null;
  return (
    <Stack
      direction="row"
      spacing={0.25}
      alignItems="center"
      sx={{ flexShrink: 0, alignSelf: "center" }}
    >
      {actions.map((action) => (
        <Tooltip key={action.key} title={action.label}>
          <IconButton
            size="small"
            aria-label={action.label}
            onClick={action.onClick}
            sx={{ p: "2px", color: "text.secondary" }}
          >
            <action.Icon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ))}
    </Stack>
  );
};

// tel: / sms: / mailto: trio shared by the Linguist, Requestor, Invoice To and
// Contact Persons rows. `phone` arrives normalized (normalizePhoneNumber) and
// may be "N/A"; the digits-only stripping mirrors InterpretationProfile's
// buildCallLink so the link stays dialable whatever the display formatting.
const buildDirectContactActions = (phone, email) => {
  const actions = [];
  const dialable =
    phone && phone !== "N/A" ? String(phone).replace(/[^\d+]/g, "") : "";
  if (dialable) {
    actions.push({
      key: "call",
      label: "Call",
      Icon: CallRoundedIcon,
      onClick: () => {
        window.location.href = `tel:${dialable}`;
      },
    });
    actions.push({
      key: "text",
      label: "Text",
      Icon: SmsRoundedIcon,
      onClick: () => {
        window.location.href = `sms:${dialable}`;
      },
    });
  }
  const normalizedEmail = typeof email === "string" ? email.trim() : "";
  if (normalizedEmail && normalizedEmail.toLowerCase() !== "n/a") {
    actions.push({
      key: "email",
      label: "Email",
      Icon: EmailRoundedIcon,
      onClick: () => {
        window.location.href = `mailto:${normalizedEmail}`;
      },
    });
  }
  return actions;
};

// Google Maps search deep link for a free-text address (keyless universal
// link; opens the maps app on mobile).
const buildMapsSearchUrl = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    String(address || "").trim(),
  )}`;

// Copy the address to the clipboard and confirm via the screen's snackbar
// (success / info pattern). Local helper — kept per-file on purpose (no
// shared cross-screen component).
const copyAddressToClipboard = (address, showSnackbar, theme) => {
  const write = async () => {
    try {
      await navigator.clipboard.writeText(String(address).trim());
      showSnackbar("Address copied", theme, "success");
    } catch {
      showSnackbar("Couldn't copy the address", theme, "info");
    }
  };
  write();
};

// Two-column grid for the short fact rows (Schedule & Delivery, Client Data,
// Costs, Rates & Units, Delivery & Payment, Status & Record). Compact cells
// use stacked label-above-value because at the 320px minimum panel width a
// half-width cell cannot hold SidePanelField's fixed 104px label column.
const SidePanelFieldGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      columnGap: 2,
      rowGap: 0.25,
    }}
  >
    {children}
  </Box>
);

// Full-width cell inside a SidePanelFieldGrid (e.g. Cancellation Reason).
const SidePanelGridFullRow = ({ children }) => (
  <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>{children}</Box>
);

// Compact grid-cell variant of SidePanelField: small caption label with the
// value line below it. Same N/A fallback and overflow rules as SidePanelField.
const SidePanelCompactField = ({ label, value, actions = null }) => (
  <Box sx={{ py: 0.5, minWidth: 0 }}>
    <Typography
      component="div"
      sx={{ color: "text.secondary", fontSize: "0.72rem", lineHeight: 1.2 }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        minWidth: 0,
        mt: "2px",
      }}
    >
      {React.isValidElement(value) ? (
        value
      ) : (
        <Typography
          component="div"
          sx={{
            minWidth: 0,
            overflowWrap: "anywhere",
            fontSize: "0.9375rem",
            fontWeight: 600,
            color: "text.primary",
            lineHeight: 1.35,
          }}
        >
          {value === "" || value === undefined || value === null
            ? "N/A"
            : value}
        </Typography>
      )}
      <SidePanelActions actions={actions} />
    </Box>
  </Box>
);

// A two-column label/value row. `value` may be a React node (e.g. the
// clamped Details block); plain strings fall back to "N/A" when empty.
// `actions` renders small icon buttons after the value (same line).
const SidePanelField = ({ label, value, multiline = false, actions = null }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: multiline ? "flex-start" : "baseline",
      gap: 1,
      py: 0.5,
    }}
  >
    <Typography
      component="span"
      sx={{
        color: "text.secondary",
        flexShrink: 0,
        width: 104,
        fontSize: "0.8125rem",
      }}
    >
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {React.isValidElement(value) ? (
        value
      ) : (
        <Typography
          component="div"
          sx={{
            overflowWrap: "anywhere",
            whiteSpace: multiline ? "pre-wrap" : "normal",
            fontSize: "0.9375rem",
            fontWeight: 500,
            color: "text.primary",
            lineHeight: 1.45,
          }}
        >
          {value === "" || value === undefined || value === null
            ? "N/A"
            : value}
        </Typography>
      )}
    </Box>
    <SidePanelActions actions={actions} />
  </Box>
);

const Translations = () => {
  usePageTitle("Translations");
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const navigate = useNavigate();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const [isLoading, setIsLoading] = useState(true);

  // Grid options state
  const getInitialDensity = () => {
    const saved = localStorage.getItem("TRANSLATIONS_GRID_DENSITY");
    return saved || "standard";
  };
  const getInitialColumnsVisible = () => {
    const saved = localStorage.getItem("TRANSLATIONS_COLUMNS_VISIBLE");
    const parsed = saved ? JSON.parse(saved) : {};

    return {
      ...parsed,
      LinguistPay: true,
      TotalLinguistPay: true,
      AdditionalCost: true,
      AdditionalLinguistCost: true,
      EquipmentCost: true,
      DeliveryCost: true,
      PaymentFees: true,
      NotarizationOrCOTCost: true,
    };
  };
  const [density, setDensity] = useState(getInitialDensity());
  const [columnsVisible, setColumnsVisible] = useState(
    getInitialColumnsVisible(),
  );

  // Grid API ref for DataGrid exports
  const apiRef = useGridApiRef();

  // Overlay state for Density & Columns
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayMode, setOverlayMode] = useState(null); // 'density' or 'columns'

  // Save density to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("TRANSLATIONS_GRID_DENSITY", density);
  }, [density]);

  // Save columns visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "TRANSLATIONS_COLUMNS_VISIBLE",
      JSON.stringify(columnsVisible),
    );
  }, [columnsVisible]);

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  // Set up entity tracking for Translations list to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to TranslationsList so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "TranslationsList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  const { setOverlayData, setOverlayName } =
    useStateContext();
  const { refreshPage, showSnackbar } = useStateContext();

  const [minMaxDates, setMinMaxDates] = useState();

  // Month selector data (static order)
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Get saved months from localStorage or use current month as default
  const getSavedMonths = () => {
    const savedMonths = localStorage.getItem(CONST.TRANSLATION_MONTHS_KEY);
    return savedMonths ? JSON.parse(savedMonths) : [dayjs().format("MMM")];
  };

  // ── 2026-10-30 — Deadline-alert deep link (?ids=…&months=YYYY-MM) ──────────
  // The URL range seeds the INITIAL month/year state so the very first fetch
  // already targets the alert's month instead of the saved default, but it is
  // never written back to localStorage. `urlIdFilter` is the transient row
  // predicate; `urlRangeApplied` remembers that the range came from the URL so
  // Clear can restore the user's saved preference.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlMonthRange = parseUrlMonthRange(searchParams.get("months"));
  const [urlIdFilter, setUrlIdFilter] = useState(() =>
    parseUrlIdList(searchParams.get("ids")),
  );
  const [urlRangeApplied, setUrlRangeApplied] = useState(!!urlMonthRange);
  // True while a deadline-alert deep link drives the list — shows the notice.
  const urlFilterActive = !!urlIdFilter || urlRangeApplied;

  const [selectedMonths, setSelectedMonths] = useState(() =>
    urlMonthRange ? urlMonthRange.months : getSavedMonths(),
  );
  const [tempSelectedMonths, setTempSelectedMonths] = useState(() =>
    urlMonthRange ? urlMonthRange.months : getSavedMonths(),
  );

  // Year selector data - dynamically generated from minMaxDates
  const currentYear = dayjs().year();

  const getSavedYear = () => {
    const savedYear = localStorage.getItem(CONST.TRANSLATION_YEAR_KEY);
    return savedYear ? JSON.parse(savedYear) : [String(currentYear)];
  };

  const [yearOptions, setYearOptions] = useState([String(currentYear)]);

  const [selectedYear, setSelectedYear] = useState(() =>
    urlMonthRange ? [urlMonthRange.year] : getSavedYear(),
  );
  const [tempSelectedYear, setTempSelectedYear] = useState(() =>
    urlMonthRange ? [urlMonthRange.year] : getSavedYear(),
  );

  const [, setErrorMessage] = useState("");
  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  const [listOfClients, setListOfClients] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);
  const [latestProjectNumber, setLatestProjectNumber] = useState(0);
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [rateTypes, setRateTypes] = useState(0);
  const [isDataListsReady, setIsDataListsReady] = useState(false);
  const [pendingCreateNew, setPendingCreateNew] = useState(false);
  const fetchTranslationsDataRef = useRef(() => {});
  const getDataListsRef = useRef(() => {});
  const hasInitializedDataRef = useRef(false);
  // Scroll position to restore after a refetch (saves bounce the list to top).
  // The DataGrid's virtual scroller is the ONLY scroller on this page (the
  // page itself never scrolls), so we save its pixel offset plus the first
  // visible ROW id — the restore anchors to that row via the grid API, which
  // re-syncs the virtualizer (a raw scrollTop write can leave the scrollbar
  // mid-track while the grid renders from index 0).
  const listScrollRestoreRef = useRef(0);
  const listFirstVisibleRowIdRef = useRef(null);

  // Deadline-alert deep links: re-apply when the params change while this
  // screen stays mounted (clicking a second pill). The guard ref is SEEDED
  // with the first render's signature, so the mount fetch — which already used
  // the URL-seeded month state — is not duplicated; only a genuinely new URL
  // signature runs the body, and the body never writes the URL, so it cannot
  // loop.
  const urlFilterSignature = `${searchParams.get("ids") || ""}|${
    searchParams.get("months") || ""
  }`;
  const appliedUrlSignatureRef = useRef(urlFilterSignature);

  useEffect(() => {
    if (appliedUrlSignatureRef.current === urlFilterSignature) {
      return;
    }
    appliedUrlSignatureRef.current = urlFilterSignature;

    const nextIds = parseUrlIdList(searchParams.get("ids"));
    const nextRange = parseUrlMonthRange(searchParams.get("months"));

    setUrlIdFilter(nextIds);
    setUrlRangeApplied(!!nextRange);

    if (nextRange) {
      // Set state DIRECTLY: handleDateSelectionSave (the only writer of the
      // saved month/year keys) is bypassed, so the user's preference survives.
      setSelectedMonths(nextRange.months);
      setTempSelectedMonths(nextRange.months);
      setSelectedYear([nextRange.year]);
      setTempSelectedYear([nextRange.year]);
      fetchTranslationsDataRef.current?.(nextRange.months, [nextRange.year]);
    }
  }, [urlFilterSignature, searchParams]);

  // ── 2026-11-03 (Amine) — no quick date-range here ──────────────────────────
  // The presets live ONLY on the Interpretations list. Translations and Other
  // Services are dated by `Deadline`, not `Date`, so the same five buttons
  // would mean something different on each screen — the inconsistency was
  // worse than the convenience. Deep links from the dashboard still work.

  const [contextMenu, setContextMenu] = useState(null);
  const [listOfServices, setListOfServices] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();

  // Status submenu state (context-menu "Status >" item)
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const statusMenuCloseTimerRef = useRef(null);

  // Derived selection for context menu actions
  const selectedTranslation = useMemo(() => {
    return (listOfServices || []).find(({ id }) => id === selectedRow);
  }, [listOfServices, selectedRow]);

  const canCompleteSelected =
    selectedTranslation &&
    selectedTranslation.TranslationStatus !== "Completed";

  // 2026-10-13 (Amine) — side-panel ⋮ menu mirrors the Assignment Profile's
  // Actions panel, whose gates are stricter than the grid context menu's:
  // Complete hides for Completed AND Cancelled statuses (profile condition),
  // and the Paid/Unpaid pair follows the profile's exact definitions
  // (`InvoiceID` is on every list row; the `Invoice` fallback keeps the
  // profile's shape for deeper payloads).
  const canCompletePanelSelected = Boolean(
    selectedTranslation &&
      !(
        selectedTranslation.TranslationStatus &&
        (selectedTranslation.TranslationStatus.includes("Completed") ||
          selectedTranslation.TranslationStatus.includes("Cancelled"))
      ),
  );
  const hasLinkedInvoiceSelected = Boolean(
    selectedTranslation?.Invoice?.id || selectedTranslation?.InvoiceID,
  );
  const canMarkPanelSelectedAsPaid = Boolean(
    selectedTranslation &&
      !selectedTranslation.Paid &&
      !selectedTranslation.Invoiced,
  );
  const canMarkPanelSelectedAsUnpaid = Boolean(
    selectedTranslation?.Paid && !hasLinkedInvoiceSelected,
  );

  // Statuses offered in the context-menu submenu (Cancelled variants excluded)
  const statusOptions = ["In-Progress", "Delivered", "Completed"];
  const currentStatus = selectedTranslation?.TranslationStatus;

  // Selection controls
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const longPressTimerRef = useRef(null);
  // Round-7 tap-bulletproofing (Amine, 4th on-device report): the long-press
  // ghost-click gate is a TIME WINDOW, not a boolean. A missed clear path on
  // a boolean swallowed the next tap forever; a timestamp expires
  // structurally shortly after the long-press that opened it.
  const suppressClickUntilRef = useRef(0);
  // Stamps the last touchend so the row handlers can tell the synthetic
  // mouse events that follow a touch (mousedown → mouseup → click) apart
  // from a genuine new press.
  const suppressTouchGraceRef = useRef(0);
  const LONG_PRESS_MS = 500;
  // Round-5 tap fix (Amine: "I tapped an item card, and it didn't open"):
  // a long-press suppress guard whose ghost click never arrived (Android
  // contextmenu, drag-off, pointercancel) must never swallow the NEXT tap.
  // A mousedown older than this since the last touchend is a real press.
  const GHOST_MOUSE_MS = 1000;
  // Round-7: window for the ghost click that follows a long-press — it
  // lands within a few frames of the release; 700ms covers slow devices
  // and can never block a later, genuine tap.
  const GHOST_CLICK_WINDOW_MS = 700;

  // 2026-10-12 (Amine) — Mini profile side panel (desktop single-click),
  // ported from the Interpretations list. The row is derived live from
  // listOfServices so socket refetches update the panel in place; a row
  // that leaves the list closes the panel structurally.
  const [sidePanelRowId, setSidePanelRowId] = useState(null);

  const sidePanelRow = useMemo(
    () =>
      (listOfServices || []).find((row) => row.id === sidePanelRowId) || null,
    [listOfServices, sidePanelRowId],
  );

  // 2026-10-12 (Amine) — side-panel footer "more actions" (⋮) menu anchor.
  // Mirrors the row context menu for the panel's row; dismissed when the
  // panel closes or swaps rows.
  const [panelMenuAnchor, setPanelMenuAnchor] = useState(null);

  // Resizable docked panel. The width lives on the panel only: during a
  // drag the handle updates the panel's DOM width directly (no grid
  // re-render) and commits a single state update on release; the final
  // width persists across reloads. `null` = no manual override → the panel
  // follows the breakpoint default; double-click resets.
  const [sidePanelWidth, setSidePanelWidth] = useState(() => {
    const saved = Number(localStorage.getItem(SIDE_PANEL_WIDTH_STORAGE_KEY));
    return Number.isFinite(saved) && saved > 0
      ? clampSidePanelWidth(saved)
      : null;
  });
  const [isSidePanelResizing, setIsSidePanelResizing] = useState(false);
  const sidePanelGridRef = useRef(null);
  const sidePanelRef = useRef(null);

  // Breakpoint-responsive default width: bigger screens get a bigger panel.
  // Only used while no manual override exists.
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const isXlUp = useMediaQuery(theme.breakpoints.up("xl"));
  const responsiveSidePanelWidth = isXlUp ? 560 : isLgUp ? 480 : isMdUp ? 420 : 380;

  // Viewport mirror — keeps the container cap fresh for stored/dragged
  // overrides on small windows (window resize → re-render → fresh cap).
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    const handleViewportResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleViewportResize);
    return () => window.removeEventListener("resize", handleViewportResize);
  }, []);

  // Cap: never let the panel swallow the whole grid — keep at least ~120px
  // of DataGrid visible, hard-capped at the max width.
  const sidePanelMaxWidth = Math.max(
    SIDE_PANEL_MIN_WIDTH,
    Math.min(
      SIDE_PANEL_MAX_WIDTH,
      (sidePanelGridRef.current?.clientWidth || viewportWidth) - 120,
    ),
  );
  // The width actually rendered: manual override wins, else the breakpoint
  // default; always clamped into [min, max].
  const effectiveSidePanelWidth = Math.max(
    SIDE_PANEL_MIN_WIDTH,
    Math.min(sidePanelWidth ?? responsiveSidePanelWidth, sidePanelMaxWidth),
  );

  const handleSidePanelResizeStart = (event) => {
    event.preventDefault();
    if (event.currentTarget && event.currentTarget.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture is best-effort; the window listeners below are the
        // source of truth for move/up.
      }
    }

    const startX = event.clientX;
    // Drag starts from the width currently on screen: the manual override
    // when present, otherwise the breakpoint-responsive default (null-safe —
    // `effectiveSidePanelWidth` is always a number).
    const startWidth = effectiveSidePanelWidth;
    const widthFor = (clientX) => {
      // Fresh cap per call — the grid/window can change size mid-drag.
      const maxAllowed = Math.max(
        SIDE_PANEL_MIN_WIDTH,
        Math.min(
          SIDE_PANEL_MAX_WIDTH,
          (sidePanelGridRef.current?.clientWidth || viewportWidth) - 120,
        ),
      );
      return Math.round(
        Math.min(
          maxAllowed,
          Math.max(SIDE_PANEL_MIN_WIDTH, startWidth + (startX - clientX)),
        ),
      );
    };
    let lastClientX = startX;

    const handlePointerMove = (moveEvent) => {
      lastClientX = moveEvent.clientX;
      if (sidePanelRef.current) {
        sidePanelRef.current.style.width = `${widthFor(moveEvent.clientX)}px`;
      }
    };
    const handlePointerEnd = () => {
      const finalWidth = widthFor(lastClientX);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerEnd);
      window.removeEventListener("pointercancel", handlePointerEnd);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      if (sidePanelRef.current) {
        sidePanelRef.current.style.width = "";
      }
      localStorage.setItem(SIDE_PANEL_WIDTH_STORAGE_KEY, String(finalWidth));
      setIsSidePanelResizing(false);
      setSidePanelWidth(finalWidth);
    };

    setIsSidePanelResizing(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "col-resize";
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerEnd);
    window.addEventListener("pointercancel", handlePointerEnd);
  };

  // Long-press arms bulk selection; the mini profile panel targets one row
  // and closes as soon as selection arms (same dismissal semantics as the
  // context menu). Desktop single-click does not open it while armed.
  useEffect(() => {
    if (selectionEnabled) {
      setSidePanelRowId(null);
    }
  }, [selectionEnabled]);

  // The side panel is desktop-only. Crossing into a mobile viewport
  // (shrinking the window below 600px) drops the open row so hidden-panel
  // state can't linger; the render condition is the hard guard, this just
  // keeps state clean.
  useEffect(() => {
    if (isMobile && sidePanelRowId != null) {
      setSidePanelRowId(null);
    }
  }, [isMobile, sidePanelRowId]);

  // 2026-10-12 (Amine) — the footer ⋮ menu belongs to the panel's row:
  // closing the panel or swapping to another row dismisses it.
  useEffect(() => {
    setPanelMenuAnchor(null);
  }, [sidePanelRowId]);

  // 2026-10-12 — id → linguist lookup for the side panel quick actions,
  // built from the same brief list the New/Edit overlay receives (the brief
  // record is the richer phone/email source when the row's own field is
  // empty).
  const linguistsById = useMemo(() => {
    const map = {};
    (listOfLinguists || []).forEach((linguist) => {
      if (linguist && linguist.id !== undefined) {
        map[linguist.id] = linguist;
      }
    });
    return map;
  }, [listOfLinguists]);

  // 2026-10-12 — Mileage rate table for the side panel Checks (same
  // endpoint + failure tolerance as the Interpretations list): fetch failure
  // degrades to an empty array so rate-based checks skip silently.
  const [mileageRates, setMileageRates] = useState([]);
  useEffect(() => {
    api
      .get("/mileageRates")
      .then((response) => {
        setMileageRates(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        setMileageRates([]);
      });
  }, []);

  // 2026-10-12 — Settings check config for the panel Checks section
  // (module-cached, one fetch per session). Failure → null → the panel
  // shows the neutral "no checks set up" line instead of claiming passes.
  const [checkConfig, setCheckConfig] = useState(null);
  useEffect(() => {
    let cancelled = false;

    loadValidationCheckConfig().then((config) => {
      if (!cancelled && Array.isArray(config)) {
        setCheckConfig(config);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // 2026-10-12 — "are any checks configured?" oracle for the side panel's
  // empty state: at least one ENABLED config row (built-in, converted or
  // custom) whose CSV ServiceCategory scope covers Translation. The engine
  // receives the FULL config (it applies scope per check itself); a null
  // config (load failure) reads as "not set up", never as "all passed".
  const translationChecksConfigured = useMemo(() => {
    if (!Array.isArray(checkConfig)) return false;
    return checkConfig.some(
      (row) =>
        row &&
        typeof row === "object" &&
        String(row.Code || "").trim() !== "" &&
        !isCheckDisabledFlag(row.IsEnabled) &&
        checkAppliesToCategory(row, "Translation"),
    );
  }, [checkConfig]);

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    // Convert field name to uppercase and create key
    // e.g., "Client" -> "TRANSLATIONS_FILTER_CLIENT"
    const upperFieldName = fieldName.toUpperCase();
    return `TRANSLATIONS_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback(
    (fieldName, values) => {
      const key = getFilterStorageKey(fieldName);
      if (values && values.length > 0) {
        localStorage.setItem(key, JSON.stringify(values));
      } else {
        // Remove from localStorage if no values selected
        localStorage.removeItem(key);
      }
    },
    [getFilterStorageKey],
  );

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback(
    (fieldName) => {
      const key = getFilterStorageKey(fieldName);
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : [];
    },
    [getFilterStorageKey],
  );

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    // Get all column field names
    const fields = [
      "ProjectNumber",
      "Date",
      "Deadline",
      "Client",
      "Requestor",
      "TranslationStatus",
      "AccountCharge",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Linguist",
      "Language",
      "TargetLanguage",
      "Profit",
      "Margin",
      "TranslationType",
      "Rate",
      "Cancelled",
      "CancellationReason",
      "Invoiced",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
      "Checks",
    ];

    fields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "ProjectNumber",
      "Date",
      "Deadline",
      "Client",
      "Requestor",
      "TranslationStatus",
      "AccountCharge",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Linguist",
      "Language",
      "TargetLanguage",
      "Profit",
      "Margin",
      "TranslationType",
      "Rate",
      "Cancelled",
      "CancellationReason",
      "Invoiced",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
      "Checks",
    ];

    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  const handleContextMenu = (event) => {
    event.preventDefault();

    const rowId = Number(event.currentTarget.getAttribute("data-id"));
    setSelectedRow(rowId);

    if (Number.isFinite(rowId)) {
      setSelectionModel((prev) => {
        if (selectionEnabled) {
          return prev.includes(rowId) ? prev : [...prev, rowId];
        }
        return [rowId];
      });
    }

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const handleContextMenuEdit = () => {
    api
      .get(`/services/translations/${selectedRow}`)
      .then((response) => {
        const translationData = response.data;

        const secondData = {
          ...translationData,
          Action: OVERLAY_NAMES.TRANSLATION_ACTION_EDIT,
          Translations: listOfServices,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          TranslationStatuses: listOfStatuses,
          TranslationTypes: listOfTypes,
          RateTypes: rateTypes,
        };

        setOverlayData(secondData);
        setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuAddTask = () => {
    const translationData = listOfServices.find(({ id }) => id === selectedRow);

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Service",
        EntityID: selectedRow,
        ServiceType: "Translation",
      },
      Service: {
        id: translationData.id,
        ProjectNumber: translationData.ProjectNumber,
        Details: translationData.Details,
        ServiceCategory: translationData.ServiceCategory,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  const handleContextMenuAddNote = () => {
    const data = {
      ServiceID: selectedRow,
      MemberID: currentUser.id,
      ModelType: "Translation",
      Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_NEW,
    };

    setOverlayData(data);

    setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);

    setContextMenu(null);
  };

  const extractCostLinesFromService = (costLines) => {
    if (!Array.isArray(costLines) || costLines.length === 0) {
      return [];
    }

    return costLines.map((line) => ({
      CostTypeName: line.CostType?.Name || line.CostTypeName || "",
      SourceLegacyField: line.SourceLegacyField || null,
      Amount: Number(line.Amount || 0).toFixed(2),
      IncludeInTotalCost: line.IncludeInTotalCost ?? true,
      IncludeInAccountCharge: line.IncludeInAccountCharge ?? false,
      DisplayOrder: line.DisplayOrder || 0,
    }));
  };

  const handleContextMenuDuplicateConfirm = () => {
    api
      .get(`/services/translations/${selectedRow}`)
      .then(async (response) => {
        const translationData = response.data;

        if (!translationData) return;

        let fetchedCostLines = [];
        try {
          const costLinesResponse = await api.get(
            `/serviceCosts/service/${selectedRow}/lines`,
          );
          fetchedCostLines = Array.isArray(costLinesResponse.data?.lines)
            ? costLinesResponse.data.lines
            : [];
        } catch (error) {
          console.error("Error loading translation cost lines for duplicate:", error);
        }

        const duplicateCostLines = extractCostLinesFromService(
          fetchedCostLines.length > 0
            ? fetchedCostLines
            : Array.isArray(translationData.CostLines)
              ? translationData.CostLines
              : [],
        );

        // Extract target languages
        const targetLanguages =
          translationData.ServiceTargetLanguages &&
          translationData.ServiceTargetLanguages.length > 0
            ? translationData.ServiceTargetLanguages.map((tl) => tl.Language)
            : [];

        const normalizedUnitTypeId = translationData.UnitTypeID || null;
        const normalizedUnitCount =
          translationData.UnitCount ?? translationData.WordCount ?? null;
        const legacyWordCount =
          translationData.UnitType?.Name === "Word"
            ? normalizedUnitCount
            : (translationData.WordCount ?? null);

        // 2026-10-20 (Amine): Translations and Other Services use their own
        // SEQUENTIAL series (104307, 104308, …) — NOT the date-based
        // Interpretation numbers (26102003). Resolve exactly the way the
        // Add Translation screen does: latest number in the series + 1.
        let duplicateProjectNumber = latestProjectNumber;
        try {
          const latestResponse = await api.get(
            "/services/translations/latestProjectNumber",
          );
          const latestValue = Number(latestResponse.data?.ProjectNumber || 0);
          if (Number.isFinite(latestValue) && latestValue > 0) {
            duplicateProjectNumber = String(latestValue + 1);
          }
        } catch (error) {
          console.error(
            "Error resolving the next translation project number:",
            error,
          );
        }

        const duplicatedData = {
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          Translations: listOfServices,
          TranslationStatuses: listOfStatuses,
          TranslationTypes: listOfTypes,
          RateTypes: rateTypes,
          ProjectNumber: duplicateProjectNumber,
          Action: OVERLAY_NAMES.TRANSLATION_ACTION_NEW,
          PrefilledData: {
            Date: translationData.Date,
            Deadline: translationData.Deadline || null,
            DeliveryDate: translationData.DeliveryDate || null,
            Name: translationData.Name,
            Details: translationData.Details,
            TranslationStatus: translationData.TranslationStatus,
            AccountCharge: translationData.AccountCharge,
            TotalCost: translationData.TotalCost,
            TotalLinguistPay: translationData.TotalLinguistPay,
            AdditionalCost: translationData.AdditionalCost,
            AdditionalLinguistCost: translationData.AdditionalLinguistCost,
            LinguistPay: translationData.LinguistPay,
            NotarizationOrCOTCost: translationData.NotarizationOrCOTCost,
            DeliveryCost: translationData.DeliveryCost,
            DeliveryType: translationData.DeliveryType,
            PaymentFees: translationData.PaymentFees,
            PaymentType: translationData.PaymentType,
            Profit: translationData.Profit,
            Margin: translationData.Margin,
            TranslationType: translationData.ServiceSubtype?.Name || null,
            RateType: translationData.RateType,
            Rate: translationData.Rate,
            UnitTypeID: normalizedUnitTypeId,
            UnitCount: normalizedUnitCount,
            WordCount: legacyWordCount,
            NumberOfLanguages: targetLanguages.length,
            SourceLanguage: translationData.Language,
            TargetLanguages: targetLanguages,
            Client: translationData.Client,
            Requestor: translationData.Requestor,
            Linguist: translationData.Linguist,
            Invoicer: translationData.Invoicer,
            CostLines: duplicateCostLines,
          },
        };

        setOverlayData(duplicatedData);
        setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
        setContextMenu(null);
        closeDialog();
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuDuplicate = () => {
    openDialog({
      theme: theme,
      title: "Duplicate Assignment",
      message:
        "Are you sure you want to duplicate this translation assignment?",
      secondaryAction: "Close",
      primaryAction: "Duplicate",
      secondaryOnClick: () => {
        closeDialog();
        setContextMenu(null);
      },
      primaryOnClick: () => {
        handleContextMenuDuplicateConfirm();
      },
    });
  };

  const handleContextMenuComplete = () => {
    api
      .get(`/services/translations/${selectedRow}`)
      .then((response) => {
        const translationData = response.data;

        const data = {
          ...translationData,
          Action: OVERLAY_NAMES.TRANSLATION_ACTION_COMPLETE,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          TranslationStatuses: listOfStatuses,
          TranslationTypes: listOfTypes,
          ProjectNumber: latestProjectNumber,
          RateTypes: rateTypes,
          DeliveryTypes: deliveryTypes,
          PaymentTypes: paymentTypes,
        };

        setOverlayData(data);
        setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  /* 2026-10-13 (Amine) — side-panel ⋮ menu handlers. The menu mirrors the
     Assignment Profile's right-column Actions panel, so each handler copies
     the profile's exact dialog copy and payload at LIST scope (refetch via
     fetchTranslationsData + snackbar instead of the profile's page
     refresh/navigate). */

  const handlePanelMarkAsChecked = () => {
    openDialog({
      theme: theme,
      title: "Mark As Checked",
      message:
        "This assignment will be marked as Checked by you on today's date",
      secondaryAction: "Close",
      primaryAction: "Confirm",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          await api.post("/services/markServiceChecked", {
            data: {
              id: selectedRow,
              CheckedBy: currentUser.id,
              CheckingDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            },
          });
          showSnackbar(
            "Translation Assignment Marked as Checked",
            theme,
            "success",
          );
          fetchTranslationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to mark the assignment as Checked.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  const handlePanelMarkAsPaid = () => {
    openDialog({
      theme: theme,
      title: "Mark As Paid",
      message:
        selectedTranslation?.Client?.Account !== "Private Persons"
          ? "This assignment will be marked as Paid, and won't be invoiced, are you sure you want to continue?"
          : "This assignment will be marked as Paid",
      secondaryAction: "Close",
      primaryAction: "Confirm",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          await api.post("/services/markServicePaid", {
            data: {
              id: selectedRow,
              Paid: true,
              Invoiced: true,
            },
          });
          showSnackbar(
            "Translation Assignment Marked as Paid",
            theme,
            "success",
          );
          fetchTranslationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to mark the assignment as Paid.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  const handlePanelMarkAsUnpaid = () => {
    openDialog({
      theme: theme,
      title: "Mark As Unpaid",
      message:
        "This assignment will be marked as Unpaid and returned to not invoiced status, are you sure you want to continue?",
      secondaryAction: "Close",
      primaryAction: "Confirm",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          await api.post("/services/markServicePaid", {
            data: {
              id: selectedRow,
              Paid: false,
              Invoiced: false,
              InvoiceID: null,
            },
          });
          showSnackbar("Assignment Marked as Unpaid", theme, "success");
          fetchTranslationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to mark the assignment as Unpaid.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  const handlePanelChangeCategory = () => {
    setOverlayData({
      ServiceID: selectedRow,
      Category: selectedTranslation?.ServiceCategory,
    });
    setOverlayName(OVERLAY_NAMES.SERVICE_CHANGE_CATEGORY);
  };

  const handlePanelDeleteAssignment = () => {
    openDialog({
      theme: theme,
      title: "Delete Assignment",
      message: "Do you really wish to delete this assignment?",
      secondaryAction: "Close",
      primaryAction: "Delete",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          await api.post("/services/deleteService", {
            data: {
              Old: {
                Assignment: {
                  id: selectedRow,
                  Deleted: 0,
                },
              },
              New: {
                Assignment: {
                  id: selectedRow,
                  Deleted: 1,
                },
              },
            },
          });

          // List-scope finish (the profile navigates back instead): close
          // the panel, clear the selection, refetch, confirm.
          setSidePanelRowId(null);
          setSelectedRow(null);
          setSelectionModel([]);
          showSnackbar(
            "Translation Assignment Deleted Successfully",
            theme,
            "success",
          );
          fetchTranslationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to delete the assignment.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  // Utilities — Copy Tracker Row. The profile builds the row from its full
  // record; list rows lack ServiceNotes / UnitType / PrivateNotes, so the
  // full record is fetched first (the same /services/translations/:id the
  // Edit and Complete flows use), then the profile's buildTrackerRow logic
  // runs on it verbatim.
  const formatTrackerDate = (value) => {
    if (!value) return "";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("M/D/YYYY") : "";
  };

  const sanitizeCell = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    return /["\t\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };

  const buildTrackerRow = (translationData) => {
    const targetLanguages =
      !translationData.ServiceTargetLanguages ||
      translationData.ServiceTargetLanguages.length === 0
        ? "N/A"
        : translationData.ServiceTargetLanguages
            .map((item) =>
              item.Language && item.Language.Name ? item.Language.Name : "",
            )
            .filter((name) => name)
            .join(" | ");

    const notesText = (translationData.ServiceNotes || [])
      .map((note) => note?.Note)
      .filter(Boolean)
      .join("\n");

    const normalizedMargin =
      translationData.Margin === null || translationData.Margin === undefined
        ? ""
        : Number(translationData.Margin) / 100;

    const notarizationCost = translationData.NotarizationOrCOTCost;
    const notarizedOrCot =
      notarizationCost && Number(notarizationCost) !== 0 ? "True" : "";

    const paidValue = translationData.Paid ? "True" : "";

    const formattedDeliveryDate = formatTrackerDate(
      translationData.DeliveryDate,
    );

    const privateNotes =
      translationData.PrivateNotes || translationData.PrivateNote || "";

    const columns = [
      formatTrackerDate(translationData.Date),
      formatTrackerDate(translationData.Deadline),
      translationData.ProjectNumber,
      translationData.Client?.Account,
      translationData.Requestor?.Name,
      translationData.TranslationStatus,
      translationData.AccountCharge,
      translationData.Details,
      translationData.Linguist?.Name,
      translationData.TotalCost,
      translationData.AdditionalCost,
      translationData.LinguistPay,
      translationData.Profit,
      normalizedMargin,
      translationData.Language?.Name,
      targetLanguages,
      "",
      notesText,
      translationData.UnitType?.Name || "N/A",
      translationData.UnitCount || translationData.WordCount || "N/A",
      translationData.NumberOfLanguages,
      "",
      translationData.Rate,
      notarizedOrCot,
      translationData.NotarizationOrCOTCost,
      translationData.DeliveryType,
      translationData.DeliveryCost,
      translationData.PaymentType,
      translationData.PaymentFees,
      formattedDeliveryDate,
      paidValue,
      translationData.ServiceSubtype?.Name,
      translationData.CancellationReason,
      privateNotes,
    ];

    return columns.map(sanitizeCell).join("\t");
  };

  const handlePanelCopyTrackerRow = () => {
    api
      .get(`/services/translations/${selectedRow}`)
      .then(async (response) => {
        const trackerRow = buildTrackerRow(response.data);
        await navigator.clipboard.writeText(trackerRow);
        showSnackbar("Tracker Row copied successfully.", theme, "success");
      })
      .catch((error) => {
        console.error("Error copying tracker row:", error);
        handleErrorDialog("Failed to copy tracker row", theme);
      });
  };

  const handleContextMenuCallRequestor = () => {
    const serviceData = listOfServices.find(({ id }) => id === selectedRow);

    const requestorPhones = serviceData?.Requestor?.PhoneNumbers || [];
    const requestorPrimary = requestorPhones.find((p) => p.IsPrimary) || requestorPhones[0];
    const requestorPhone = requestorPrimary?.Number || serviceData?.Requestor?.PhoneNumber;
    if (requestorPhone) {
      window.location.href = `tel:${requestorPhone.replace(/[^0-9+]/g, "")}`;
    }
    setContextMenu(null);
  };

  const handleContextMenuEmailRequestor = () => {
    const serviceData = listOfServices.find(({ id }) => id === selectedRow);

    if (serviceData?.Requestor?.Email) {
      const email = serviceData.Requestor.Email;
      const subject = "Regarding Assignment N° " + serviceData.ProjectNumber;
      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
        subject,
      )}`;
      window.open(mailtoLink, "_blank");
    }
    setContextMenu(null);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
    setStatusMenuAnchor(null);
  };

  // Status submenu handlers
  const handleStatusMenuOpen = (event) => {
    if (statusMenuCloseTimerRef.current) {
      clearTimeout(statusMenuCloseTimerRef.current);
      statusMenuCloseTimerRef.current = null;
    }
    setStatusMenuAnchor(event.currentTarget);
  };

  const handleStatusMenuEnter = () => {
    if (statusMenuCloseTimerRef.current) {
      clearTimeout(statusMenuCloseTimerRef.current);
      statusMenuCloseTimerRef.current = null;
    }
  };

  const handleStatusMenuClose = () => {
    if (statusMenuCloseTimerRef.current) {
      clearTimeout(statusMenuCloseTimerRef.current);
      statusMenuCloseTimerRef.current = null;
    }
    setStatusMenuAnchor(null);
  };

  const handleStatusMenuLeave = () => {
    statusMenuCloseTimerRef.current = setTimeout(() => {
      setStatusMenuAnchor(null);
    }, 150);
  };

  const handleContextMenuStatusChange = (status) => {
    setStatusMenuAnchor(null);
    setContextMenu(null);

    if (status === currentStatus) return;

    api
      .post("/services/updateStatus", {
        serviceId: selectedRow,
        status,
      })
      .then((response) => {
        const updatedService = response.data;
        showSnackbar(
          `Status updated to "${status}" for ${updatedService.ProjectNumber || "service"}.`,
          theme,
          "success",
        );

        // Refresh the list (existing UI invalidation behavior)
        fetchTranslationsData(selectedMonths, selectedYear);

        // Emit assignment saved events for other viewers (same as the edit flow)
        if (socket) {
          const userPayload = {
            id: currentUser.id,
            name: currentUser.Name,
            imageThumbnail: currentUser.ImageThumbnail,
          };
          socket.emit("assignment:saved", {
            modelType: "Translation",
            recordId: updatedService.id,
            user: userPayload,
            projectNumber: updatedService.ProjectNumber,
          });
          socket.emit("assignment:saved", {
            modelType: "TranslationsList",
            recordId: "all",
            user: userPayload,
            projectNumber: updatedService.ProjectNumber,
          });
        }
      })
      .catch((error) => {
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to update service status.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    // New-press hygiene: a leftover long-press suppress guard (its ghost
    // click never came) must not swallow this press's click. The synthetic
    // mousedown that follows a touch keeps the guard — its click is still
    // on its way (see suppressTouchGraceRef).
    if (Date.now() - suppressTouchGraceRef.current > GHOST_MOUSE_MS) {
      suppressClickUntilRef.current = 0;
    }
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressClickUntilRef.current = Date.now() + GHOST_CLICK_WINDOW_MS;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
  };

  const handleRowTouchStart = (event) => {
    // A new physical touch always starts clean: any earlier ghost click has
    // already fired (or the browser cancels it when a new touch arrives).
    // Without this, a stale guard swallows the very next card tap.
    suppressClickUntilRef.current = 0;
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressClickUntilRef.current = Date.now() + GHOST_CLICK_WINDOW_MS;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
    suppressTouchGraceRef.current = Date.now();
  };

  const handleRowSelectionModelChange = (newSelection) => {
    if (selectionEnabled) {
      setSelectionModel(newSelection);
    } else {
      // keep last selected for highlighting
      setSelectionModel(newSelection.slice(-1));
    }
  };

  const handleCancelSelection = () => {
    setSelectionModel([]);
    setSelectionEnabled(false);
    suppressClickUntilRef.current = 0;
  };

  // Auto-disarm (Amine 2026-10-05, round 6): when the ARMED selection model
  // empties through user toggling — the last selected card tapped OFF —
  // selection mode exits so the NEXT tap opens the card normally instead of
  // silently toggling again. The ref only lets the effect disarm after the
  // armed session has SEEN a non-empty model: the long-press arms
  // `selectionEnabled` and appends its id in the same callback, and the ref
  // guard keeps any transitional render (armed + empty model) from disarming
  // instantly.
  const armedSawSelectionRef = useRef(false);
  useEffect(() => {
    if (!selectionEnabled) {
      armedSawSelectionRef.current = false;
      return;
    }
    if (selectionModel.length > 0) {
      armedSawSelectionRef.current = true;
      return;
    }
    if (armedSawSelectionRef.current) {
      armedSawSelectionRef.current = false;
      setSelectionEnabled(false);
    }
  }, [selectionEnabled, selectionModel]);

  const isServiceChecked = useCallback((service) => {
    return !!(service?.CheckingDate || service?.CheckedBy || service?.Checker);
  }, []);

  const selectionStats = useMemo(() => {
    if (!selectionEnabled || selectionModel.length === 0) {
      return { total: 0, checked: 0, unchecked: 0, allChecked: false };
    }

    const selectedSet = new Set(selectionModel);
    const selectedServices = (listOfServices || []).filter((service) =>
      selectedSet.has(service.id),
    );

    const checked = selectedServices.filter(isServiceChecked).length;
    const total = selectedServices.length;
    const unchecked = Math.max(total - checked, 0);

    return {
      total,
      checked,
      unchecked,
      allChecked: total > 0 && unchecked === 0,
    };
  }, [selectionEnabled, selectionModel, listOfServices, isServiceChecked]);

  const handleMarkSelectedAsChecked = () => {
    if (selectionModel.length === 0) return;

    const selectedIds = [...selectionModel];
    const selectedServices = (listOfServices || []).filter((service) =>
      selectedIds.includes(service.id),
    );
    const uncheckedIds = selectedServices
      .filter((service) => !isServiceChecked(service))
      .map((service) => service.id);

    if (uncheckedIds.length === 0) {
      showSnackbar(
        "The selected assignment(s) are already checked.",
        theme,
        "info",
      );
      return;
    }

    const message =
      uncheckedIds.length === selectedIds.length
        ? `Mark ${selectedIds.length} assignment(s) as Checked?`
        : `Only ${uncheckedIds.length} out of ${selectedIds.length} of the selected assignments are not checked, do you want to mark them as checked?`;

    openDialog({
      theme,
      title: "Mark Assignments as Checked",
      message,
      secondaryAction: "Cancel",
      primaryAction: "Confirm",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          const payloads = uncheckedIds.map((id) => {
            const data = {
              id,
              CheckedBy: currentUser.id,
              CheckingDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            };
            return api.post("/services/markServiceChecked", {
              data,
            });
          });

          await Promise.all(payloads);
          showSnackbar("Assignments marked as Checked", theme, "success");
          handleCancelSelection();
          fetchTranslationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to mark Translations as checked.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  // Generate dynamic year options when minMaxDates is fetched
  useEffect(() => {
    if (minMaxDates && minMaxDates.startDate && minMaxDates.endDate) {
      const startYear = minMaxDates.startDate.year;
      const endYear = minMaxDates.endDate.year;
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(String(year));
      }
      setYearOptions(years);

      // Validate current selected year
      if (selectedYear.length > 0) {
        const currentSelectedYear = parseInt(selectedYear[0]);
        if (currentSelectedYear < startYear || currentSelectedYear > endYear) {
          let newYear;
          if (currentYear >= startYear && currentYear <= endYear) {
            newYear = String(currentYear);
          } else if (currentYear < startYear) {
            newYear = String(startYear);
          } else {
            newYear = String(endYear);
          }
          setSelectedYear([newYear]);
          setTempSelectedYear([newYear]);
        }
      }
    }
  }, [minMaxDates, currentYear, selectedYear]); // Add currentYear to dependencies

  // Handle date selection save (combined months and years)
  const handleDateSelectionSave = (newSelection) => {
    // Ensure only one year is selected
    const updatedSelection = {
      months: newSelection.months,
      years: newSelection.years.slice(-1), // Take only the last selected year
    };

    // Update state
    setSelectedMonths(updatedSelection.months);
    setTempSelectedMonths(updatedSelection.months);
    setSelectedYear(updatedSelection.years);
    setTempSelectedYear(updatedSelection.years);

    // Save to localStorage
    localStorage.setItem(
      CONST.TRANSLATION_MONTHS_KEY,
      JSON.stringify(updatedSelection.months),
    );
    localStorage.setItem(
      CONST.TRANSLATION_YEAR_KEY,
      JSON.stringify(updatedSelection.years),
    );

    // Fetch data
    fetchTranslationsData(updatedSelection.months, updatedSelection.years);
  };

  // Fetch translations data - memoized with useCallback to prevent listener churn
  const fetchTranslationsData = useCallback(
    (months = selectedMonths, year = selectedYear) => {
      setIsLoading(true);

      // Capture the current scroll position so a refetch (e.g. after saving
      // an edit) doesn't bounce the list back to the top. Save the virtual
      // scroller's pixel offset AND the first VISIBLE row id (anchoring to a
      // row is the reliable way to resync the virtualizer on restore).
      // Queries are scoped to THIS grid's root element via its own apiRef —
      // a global document.querySelector can match a DIFFERENT grid mounted
      // elsewhere on the page (that is why the restore worked on one list
      // but not another).
      const captureRoot = apiRef.current?.rootElementRef?.current || document;
      const scroller = captureRoot.querySelector(
        ".MuiDataGrid-virtualScroller",
      );
      if (scroller && scroller.scrollTop > 0) {
        listScrollRestoreRef.current = scroller.scrollTop;
      }
      // Only anchor by row when the user was actually scrolled down — at the
      // top we must stay at the top (a new row inserted above would otherwise
      // yank the list). Pick the first row actually VISIBLE in the viewport
      // (the first DOM row may be an off-screen buffer row above it).
      let firstVisibleRowEl = null;
      if (scroller && scroller.scrollTop > 0) {
        const scRect = scroller.getBoundingClientRect();
        const rowEls = scroller.querySelectorAll(".MuiDataGrid-row[data-id]");
        for (const el of rowEls) {
          if (el.getBoundingClientRect().bottom > scRect.top + 1) {
            firstVisibleRowEl = el;
            break;
          }
        }
      }
      listFirstVisibleRowIdRef.current = firstVisibleRowEl
        ? firstVisibleRowEl.getAttribute("data-id")
        : null;
      // Ensure parameters are valid
      if (!months || months.length === 0) {
        months = [dayjs().format("MMM")];
      }
      if (!year || year.length === 0) {
        year = [String(currentYear)];
      }

      const monthsParam = Array.isArray(months) ? months.join(",") : months;

      api
        .get("/services/translations", {
          params: {
            months: monthsParam,
            year: year[0], // Single-select year, only send first value
          },
        })
        .then((response) => {
          if (response !== null) {
            const services = response.data;
            services.reverse();
            setListOfServices(services);
            setIsLoading(false);

            // Restore the scroll position once the new rows are rendered.
            // Phase 1: materialize the anchor row (single scrollToIndexes).
            // Phase 2: nudge by the measured rect delta with a settle frame
            // between every write and the next measurement — measuring on the
            // same frame as the write reads stale layout and overshoots,
            // which caused the up/down jitter. Stops as soon as the anchor is
            // aligned, when a correction stops reducing the error, or on user
            // interaction. Scoped to THIS grid's root element via its apiRef.
            const savedRowId = listFirstVisibleRowIdRef.current;
            const savedPixel = listScrollRestoreRef.current;
            if (savedRowId != null || savedPixel > 0) {
              const targetIdx =
                savedRowId != null
                  ? services.findIndex(
                      (s) => String(s.id) === String(savedRowId),
                    )
                  : -1;

              const getScroller = () => {
                const root = apiRef.current?.rootElementRef?.current;
                return (root || document).querySelector(
                  ".MuiDataGrid-virtualScroller",
                );
              };
              let cancelled = false;
              const cancelOnInteraction = () => {
                cancelled = true;
              };
              const interactionEvents = ["wheel", "mousedown", "touchstart"];
              const initialScroller = getScroller();
              interactionEvents.forEach((evt) => {
                initialScroller?.addEventListener(evt, cancelOnInteraction, {
                  once: true,
                  passive: true,
                });
              });

              const cleanup = () => {
                listScrollRestoreRef.current = 0;
                listFirstVisibleRowIdRef.current = null;
                const sc = getScroller();
                interactionEvents.forEach((evt) => {
                  sc?.removeEventListener(evt, cancelOnInteraction);
                });
              };

              let materialized = targetIdx < 0;
              let settleFrames = 0;
              let attempts = 0;
              let lastAbsDelta = null;
              const maxAttempts = 40;

              const step = () => {
                if (cancelled) {
                  cleanup();
                  return;
                }

                const sc = getScroller();
                if (!sc) {
                  attempts += 1;
                  if (attempts < maxAttempts) requestAnimationFrame(step);
                  else cleanup();
                  return;
                }

                // Phase 1 — bring the anchor row into the render window once.
                if (!materialized) {
                  apiRef.current?.scrollToIndexes?.({ rowIndex: targetIdx });
                  materialized = true;
                  settleFrames = 1;
                  requestAnimationFrame(step);
                  return;
                }

                // Let the grid apply the last write before measuring again.
                if (settleFrames > 0) {
                  settleFrames -= 1;
                  requestAnimationFrame(step);
                  return;
                }

                if (targetIdx >= 0) {
                  const rowEl = sc.querySelector(
                    `.MuiDataGrid-row[data-id="${savedRowId}"]`,
                  );
                  if (!rowEl) {
                    // Virtualizer still settling — wait (bounded).
                    attempts += 1;
                    if (attempts < maxAttempts) requestAnimationFrame(step);
                    else cleanup();
                    return;
                  }

                  const delta =
                    rowEl.getBoundingClientRect().top -
                    sc.getBoundingClientRect().top;
                  const absDelta = Math.abs(delta);
                  if (absDelta <= 2) {
                    cleanup();
                    return;
                  }
                  // If the previous nudge did not reduce the error, stop —
                  // re-asserting would just jitter between two positions.
                  if (lastAbsDelta !== null && absDelta >= lastAbsDelta - 1) {
                    cleanup();
                    return;
                  }
                  lastAbsDelta = absDelta;
                  sc.scrollTop += delta;
                  settleFrames = 1;
                } else {
                  // No usable row anchor — restore the pixel offset and nudge
                  // the virtualizer with a scroll event (a no-op scrollTop
                  // write fires no event, so the render window never updates).
                  const pxDiff = Math.abs(sc.scrollTop - savedPixel);
                  if (pxDiff <= 2) {
                    cleanup();
                    return;
                  }
                  if (lastAbsDelta !== null && pxDiff >= lastAbsDelta - 1) {
                    cleanup();
                    return;
                  }
                  lastAbsDelta = pxDiff;
                  sc.scrollTop = savedPixel;
                  sc.dispatchEvent(new Event("scroll"));
                  settleFrames = 1;
                }

                attempts += 1;
                if (attempts < maxAttempts && !cancelled) {
                  requestAnimationFrame(step);
                } else {
                  cleanup();
                }
              };
              requestAnimationFrame(step);
            }
          }
        })
        .catch((error) => {
          const message =
            error.response?.data?.message ||
            "Unable to connect to the server. Please try again later.";
          handleErrorDialog(message, theme);

          setIsLoading(false);
        });
    },
    [selectedMonths, selectedYear, currentYear, apiRef, handleErrorDialog, theme],
  );

  // Listener callback for translations list refetch
  const handleTranslationsListRefetch = useCallback(
    (updatedBy) => {
      setLastUpdate(updatedBy); // Store for snackbar
      fetchTranslationsData(); // Refetch data
    },
    [fetchTranslationsData],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = lastUpdate?.projectNumber
        ? `#${lastUpdate.projectNumber} updated by ${lastUpdate.userName}`
        : `Translation updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  // IDs currently visible in the filtered list — updates for records outside
  // this set are ignored (a May change doesn't reload a list filtered to August)
  const visibleServiceIds = useMemo(
    () => (listOfServices || []).map((s) => Number(s.id)),
    [listOfServices],
  );

  useAssignmentUpdates({
    modelType: "TranslationsList",
    recordId: "all",
    onUpdate: handleTranslationsListRefetch,
    visibleRecordIds: visibleServiceIds,
  });

  const getDataLists = useCallback(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/translations/statuses"),
        api.get("/services/translations/types"),
        api.get("/services/translations/latestProjectNumber"),
        api.get("/services/translations/rateTypes"),
        api.get("/dashboard/serviceDateRange"),
        api.get("/services/translations/deliveryTypes"),
        api.get("/services/translations/paymentTypes"),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
            latestProjectNumberResponse,
            rateTypesResponse,
            minMaxDatesResponse,
            deliveryTypesResponse,
            paymentTypesResponse,
          ) => {
            if (clientsResponse) {
              setListOfClients(clientsResponse.data);
            }
            if (linguistsResponse) {
              setListOfLinguists(linguistsResponse.data);
            }
            if (languagesResponse) {
              const tempLanguages = languagesResponse.data.filter(
                (language) => language.Name !== "ALL",
              );
              setListOfLanguages(tempLanguages);
            }
            if (statusesResponse) {
              setListOfStatuses(statusesResponse.data);
            }
            if (typesResponse) {
              const tempTypes = typesResponse.data.slice(0, 5);
              setListOfTypes(tempTypes);
            }
            if (
              latestProjectNumberResponse &&
              latestProjectNumberResponse.data
            ) {
              setLatestProjectNumber(
                Number(latestProjectNumberResponse.data.ProjectNumber || 0) + 1,
              );
            }
            if (rateTypesResponse) {
              setRateTypes(rateTypesResponse.data);
            }
            if (minMaxDatesResponse) {
              setMinMaxDates(minMaxDatesResponse.data);
            }
            if (deliveryTypesResponse) {
              const filteredDeliveryTypes = deliveryTypesResponse.data.filter(
                (type) => !["CODA", "SmartCat", "In-person"].includes(type),
              );
              setDeliveryTypes(filteredDeliveryTypes);
            }
            if (paymentTypesResponse) {
              setPaymentTypes(paymentTypesResponse.data);
            }
            // Mark data lists as ready
            setIsDataListsReady(true);
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        setErrorMessage(message);
        handleErrorDialog(message, theme);
        setIsDataListsReady(true); // Even on error, mark as ready to prevent infinite waiting
      });
  }, [handleErrorDialog, theme]);

  fetchTranslationsDataRef.current = fetchTranslationsData;
  getDataListsRef.current = getDataLists;

  useEffect(() => {
    fetchTranslationsDataRef.current();
    getDataListsRef.current();
    hasInitializedDataRef.current = true;
  }, []);

  useEffect(() => {
    if (!hasInitializedDataRef.current) {
      return;
    }

    fetchTranslationsDataRef.current();
  }, [refreshPage]);

  // Handle pending create new action when data lists are ready
  useEffect(() => {
    if (pendingCreateNew && isDataListsReady) {
      const data = {
        Clients: listOfClients,
        Linguists: listOfLinguists,
        Languages: listOfLanguages,
        Translations: listOfServices,
        TranslationStatuses: listOfStatuses,
        TranslationTypes: listOfTypes,
        ProjectNumber: latestProjectNumber,
        RateTypes: rateTypes,

        Action: OVERLAY_NAMES.TRANSLATION_ACTION_NEW,
      };

      setOverlayData(data);
      setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
      setPendingCreateNew(false);
    }
  }, [
    pendingCreateNew,
    isDataListsReady,
    listOfClients,
    listOfLinguists,
    listOfLanguages,
    listOfServices,
    listOfStatuses,
    listOfTypes,
    latestProjectNumber,
    rateTypes,
    setOverlayData,
    setOverlayName,
  ]);

  // Sync temporary states with actual state (if changed externally)
  useEffect(() => {
    setTempSelectedMonths(selectedMonths);
  }, [selectedMonths]);

  useEffect(() => {
    setTempSelectedYear(selectedYear);
  }, [selectedYear]);

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return dayjs(v1, "M/D/YYYY").valueOf() - dayjs(v2, "M/D/YYYY").valueOf();
  };

  // 2026-10-13 (Amine) — Checks column support: one severity bucket per row
  // (Passed / Warnings / Errors) so the grid valueGetter, the header
  // ColumnFilter options and the renderCell tooltip all read a single
  // precomputed Map instead of re-running the validation engine per call.
  // Mirrors Interpretations: always evaluated (no configured-checks gate) —
  // an empty issue list is Passed / green.
  const getChecksBucket = (issues) => {
    const worst = getWorstCheckSeverity(issues);
    return worst === "error"
      ? "Errors"
      : worst === "warning"
        ? "Warnings"
        : "Passed";
  };

  const checksByRow = useMemo(() => {
    const map = new Map();
    (listOfServices || []).forEach((row) => {
      if (!row || row.id === undefined || row.id === null) return;
      const issues = runServiceChecks(row, "Translation", {
        mileageRates,
        linguistsById,
        checkConfig,
      });
      map.set(row.id, { bucket: getChecksBucket(issues), issues });
    });
    return map;
  }, [listOfServices, mileageRates, linguistsById, checkConfig]);

  // Reads the precomputed Map with a live fallback (a row missing from the
  // map is evaluated on the spot), so every consumer — valueGetter,
  // renderCell, exports — sees the same shape: { bucket, issues }.
  const getChecksEntry = useCallback(
    (row) => {
      if (!row) return { bucket: "Passed", issues: [] };
      const entry =
        row.id !== undefined && row.id !== null
          ? checksByRow.get(row.id)
          : undefined;
      if (entry) return entry;
      const issues = runServiceChecks(row, "Translation", {
        mileageRates,
        linguistsById,
        checkConfig,
      });
      return { bucket: getChecksBucket(issues), issues };
    },
    [checksByRow, mileageRates, linguistsById, checkConfig],
  );

  const columns = [
    {
      field: "ProjectNumber",
      headerName: "Number",
      minWidth: 110,
      valueGetter: (_, params) => {
        const data = params.ProjectNumber;
        const value = data ? data : "N/A";
        return value;
      },
      flex: 1.1,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            sortModel={sortModel}
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      // 2026-10-13 (Amine) — validation Checks column, second from the left
      // right after the project number. Shows the merged built-in + custom
      // check result (green / amber / red) and is FILTERABLE through the
      // standard header ColumnFilter — its options are data-driven from the
      // row buckets (Passed / Warnings / Errors), so the shared filter
      // component needs no changes. valueGetter returns the bucket for the
      // filter machinery; the CSV/XLSX exports special-case the field (raw
      // row fields would export blank — same treatment as TargetLanguage).
      // sortable: false — the header omits the sort controls and the mobile
      // filter sheet hides them via the colDef check, keeping this column
      // out of the desktop/mobile sort UI.
      // Compact fixed width (110px, flex 0) so the full header text and the filter button always render (Amine, 2026-10-13; mirrors the Interpretations column).
      field: "Checks",
      headerName: "Checks",
      minWidth: 110,
      flex: 0,
      width: 110,
      sortable: false,
      valueGetter: (_, row) => getChecksEntry(row).bucket,
      renderCell: (params) => (
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <ServiceChecksIndicator
            issues={getChecksEntry(params.row).issues}
            variant="chip"
          />
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 100,
      flex: 1.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            sortModel={sortModel}
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Deadline",
      headerName: "Deadline",
      valueGetter: (_, params) => {
        const data = params.Deadline;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 120,
      flex: 1.2,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: getDeadlineProximityColor(
                theme,
                params.value,
                params.row?.TranslationStatus,
              ),
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client;
        const value = data ? data.Account : "N/A";
        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Requestor",
      headerName: "Requestor",
      valueGetter: (_, params) => {
        const data = params.Requestor;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[4] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            sortModel={sortModel}
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TranslationStatus",
      headerName: "Status",
      minWidth: 120,
      flex: 1.2,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Box
            sx={{
              borderRadius: "8px",
              px: "0.5rem",
              backgroundColor: getStatusColor(theme, params.value),
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: theme.palette.background.default,
              }}
            >
              {params.value}
            </Typography>
          </Box>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AccountCharge",
      headerName: "Account Charge",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AccountCharge;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value =
            parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
        }

        return value;
      },
      minWidth: 165,
      flex: 1.65,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      valueGetter: (_, params) => {
        const data = params.Details;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 450,
      flex: 4.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
        {
      field: "TranslationType",
      headerName: "Type",
      valueGetter: (_, params) => {
        const data = params.ServiceSubtype?.Name;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 100,
      flex: 1.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Linguist",
      headerName: "Linguist",
      valueGetter: (_, params) => {
        const data = params.Linguist;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TotalCost",
      headerName: "Total Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.TotalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AdditionalCost",
      headerName: "Additional Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AdditionalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 160,
      flex: 1.6,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "LinguistPay",
      headerName: "Linguist Pay",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.LinguistPay;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "Profit",
      headerName: "Profit",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Profit;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 95,
      flex: 0.95,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Margin",
      headerName: "Margin",
      valueGetter: (_, params) => {
        const data = params.Margin;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          const percentage = data * 1;
          if (percentage === 0) {
            value = "0%";
          } else if (percentage === 100) {
            value = "100%";
          } else {
            value = `${percentage.toFixed(2)}%`;
          }
        }

        return value;
      },
      minWidth: 100,
      flex: 1.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Language",
      headerName: "Source Language",
      valueGetter: (_, params) => {
        const data = params.Language;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 170,
      flex: 1.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TargetLanguage",
      headerName: "Target Languages",
      valueGetter: (_, params) => {
        const languages = params.ServiceTargetLanguages;
        if (!languages || languages.length === 0) return "N/A";
        // Extract the Language.Name from each item and filter out any empty values
        return languages
          .map((item) =>
            item.Language && item.Language.Name ? item.Language.Name : "",
          )
          .filter((name) => name)
          .join(" | ");
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 300,
      flex: 3.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "UnitCount",
      headerName: "Units",
      valueGetter: (_, params) => {
        const count = params.UnitCount;

        if (count === null || count === undefined || count === "") {
          return "N/A";
        }

        const unitName = params.UnitType?.Name || "unit";
        const unitLabel =
          Number(count) === 1
            ? unitName.toLowerCase()
            : `${unitName.toLowerCase()}s`;

        return `${Number(count).toLocaleString()} ${unitLabel}`;
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "NumberOfLanguages",
      headerName: "# Of Languages",
      valueGetter: (_, params) => {
        const data = params.NumberOfLanguages;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 160,
      flex: 1.6,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Rate",
      headerName: "Rate",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Rate;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 90,
      flex: 0.9,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "RateType",
      headerName: "Rate Type",
      valueGetter: (_, params) => {
        const data = params.RateType;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "NotarizationOrCOTCost",
      headerName: "Not/COT Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.NotarizationOrCOTCost;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "DeliveryType",
      headerName: "Delivery Type",
      valueGetter: (_, params) => {
        const data = params.DeliveryType;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 155,
      flex: 1.55,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "DeliveryCost",
      headerName: "Delivery Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.DeliveryCost;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "PaymentType",
      headerName: "Payment Type",
      valueGetter: (_, params) => {
        const data = params.PaymentType;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 155,
      flex: 1.55,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "PaymentFees",
      headerName: "Payment Fees",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.PaymentFees;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 155,
      flex: 1.55,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "DeliveryDate",
      headerName: "Delivery Date",
      valueGetter: (_, params) => {
        const data = params.DeliveryDate;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 155,
      flex: 1.55,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Paid",
      headerName: "Paid",
      valueGetter: (_, params) => {
        const data = params.Paid;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 85,
      flex: 0.85,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "Cancelled",
      headerName: "Cancelled",
      valueGetter: (_, params) => {
        const data = params.Cancelled;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = data ? "Yes" : "No";
        }

        return value;
      },
      renderCell: (params) => {
        const data = params.row.Cancelled;
        if (data === undefined || data === null) return "N/A";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {data ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CancellationReason",
      headerName: "Cancellation Reason",
      valueGetter: (_, params) => {
        const data = params.CancellationReason;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      minWidth: 195,
      flex: 1.95,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "InvoiceTo",
      headerName: "Invoice To",
      valueGetter: (_, params) => {
        const data = params.Invoicer;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Invoiced",
      headerName: "Invoiced",
      valueGetter: (_, params) => {
        const data = params.Invoiced;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = data ? "Yes" : "No";
        }

        return value;
      },
      renderCell: (params) => {
        const data = params.row.Invoiced;
        if (data === undefined || data === null) return "N/A";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {data ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CreatedBy",
      headerName: "Created By",
      valueGetter: (_, params) => {
        const data = params.Creator;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CheckedBy",
      headerName: "Checked By",
      valueGetter: (_, params) => {
        const data = params.Checker;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CheckedOn",
      headerName: "Checked On",
      valueGetter: (_, params) => {
        const data = params.CheckingDate;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
  ];

  // Filter columns based on columnsVisible state
  const filteredColumns = columns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(CONST.TRANSLATION_FILTER_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  };

  const getStoredSortModel = () => {
    const stored = localStorage.getItem(CONST.TRANSLATION_SORT_KEY);
    return stored ? JSON.parse(stored) : [];
  };

  const [filterModel, setFilterModel] = useState(getStoredFilterModel);
  const [filterRowIds, setFilterRowIds] = useState([]);
  const prevFilteredRowIdsRef = useRef([]);
  const [sortModel, setSortModel] = useState(getStoredSortModel);

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);

  // Transient URL id predicate — applied to the BASE list BEFORE the saved
  // column filters, so those layer on top instead of fighting this one. Both
  // the desktop grid and the mobile card view read `columnFilteredData`, which
  // this feeds. Never persisted.
  const urlFilteredServices = useMemo(() => {
    const services = listOfServices || [];
    if (!urlIdFilter || urlIdFilter.length === 0) return services;
    const wantedIds = new Set(urlIdFilter.map(String));
    return services.filter((row) => wantedIds.has(String(row.id)));
  }, [listOfServices, urlIdFilter]);

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(urlFilteredServices, columns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback(
    (field, values) => {
      handleColumnFilterChange(field, values);
      saveColumnFilter(field, values);
    },
    [handleColumnFilterChange, saveColumnFilter],
  );

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!listOfServices || listOfServices.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [listOfServices, loadAllColumnFilters]); // Apply saved filters once per loaded dataset

  // Wrapped clear all filters that also clears localStorage.
  // 2026-10-30 (Amine): this is the ONE clear action for the screen — the grid's
  // own "Clear Filters" button calls it, so it must also drop the transient
  // deadline-alert filter from the URL. Otherwise the toolbar button would leave
  // the list still narrowed by `?ids=`.
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();

    // Clear quick filter and persist the empty model for this grid
    const clearedModel = { items: [], quickFilterValues: [] };
    setFilterModel(clearedModel);
    localStorage.setItem(
      CONST.TRANSLATION_FILTER_KEY,
      JSON.stringify(clearedModel),
    );

    // Drop the deadline-alert filter: strip the params so a refresh / back-nav
    // cannot re-apply them, and — only when the URL had overridden the range —
    // restore the user's SAVED month/year preference (never persisted).
    const restoreSavedRange = urlRangeApplied;
    setUrlIdFilter(null);
    setSearchParams({}, { replace: true });

    if (restoreSavedRange) {
      // Read the saved preference straight from storage rather than calling
      // getSavedMonths/getSavedYear — those are plain functions recreated every
      // render, and depending on them would re-create this callback each time.
      const savedMonthsRaw = localStorage.getItem(CONST.TRANSLATION_MONTHS_KEY);
      const savedMonths = savedMonthsRaw
        ? JSON.parse(savedMonthsRaw)
        : [dayjs().format("MMM")];
      const savedYearsRaw = localStorage.getItem(CONST.TRANSLATION_YEAR_KEY);
      const savedYears = savedYearsRaw
        ? JSON.parse(savedYearsRaw)
        : [String(dayjs().year())];

      setSelectedMonths(savedMonths);
      setTempSelectedMonths(savedMonths);
      setSelectedYear(savedYears);
      setTempSelectedYear(savedYears);
      fetchTranslationsDataRef.current?.(savedMonths, savedYears);
    }
  }, [
    clearAllFilters,
    clearAllColumnFilters,
    urlRangeApplied,
    setSearchParams,
  ]);

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters. The deadline-alert filter counts
  // too, so the grid's "Clear Filters" button appears when it is the only thing
  // narrowing the list.
  const hasAnyActiveFilters =
    hasActiveFilters || hasQuickFilter || urlFilterActive;

  // 2026-10-30 (Amine): announce the deadline-alert filter ONCE as a snackbar
  // instead of a standing bar above the grid. Waits for the fetch to finish so
  // the count is real, and the ref stops it re-firing while the user interacts
  // with the rows (columnFilteredData changes identity on every filter edit).
  const hasAnnouncedUrlFilterRef = useRef(false);
  useEffect(() => {
    if (!urlFilterActive) {
      hasAnnouncedUrlFilterRef.current = false;
      return;
    }

    if (isLoading || hasAnnouncedUrlFilterRef.current) return;

    const shownCount = (columnFilteredData || []).length;
    hasAnnouncedUrlFilterRef.current = true;
    // Two kinds of deep link now: an id list (a specific set of rows) and a
    // month-only link (a period summary card). Saying "from the deadline alert"
    // for both was wrong for the Dashboard's month links.
    const monthLabel = urlMonthRange
      ? `${urlMonthRange.months.join(", ")} ${urlMonthRange.year}`
      : null;
    showSnackbar(
      urlIdFilter
        ? `Showing ${shownCount} assignment${
            shownCount === 1 ? "" : "s"
          } from the dashboard link`
        : `Showing ${monthLabel}${
            shownCount > 0 ? ` — ${shownCount} assignment${shownCount === 1 ? "" : "s"}` : ""
          }`,
      theme,
      "info",
    );
  }, [
    urlFilterActive,
    urlIdFilter,
    urlMonthRange,
    isLoading,
    columnFilteredData,
    showSnackbar,
    theme,
  ]);

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (row) => row.ProjectNumber || "N/A",
      },
      {
        field: "Date",
        valueGetter: (row) =>
          row.Date ? dayjs.tz(row.Date).format("M/D/YYYY") : "N/A",
      },
      {
        field: "Deadline",
        valueGetter: (row) =>
          row.Deadline ? dayjs.tz(row.Deadline).format("M/D/YYYY") : "N/A",
      },
      {
        field: "Client",
        valueGetter: (row) => (row.Client ? row.Client?.Account : "N/A"),
      },
      {
        field: "Requestor",
        valueGetter: (row) => (row.Requestor ? row.Requestor.Name : "N/A"),
      },
      {
        field: "TranslationStatus",
        valueGetter: (row) => row.TranslationStatus || "N/A",
      },
      {
        field: "AccountCharge",
        valueGetter: (row) => {
          const data = row.AccountCharge;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
          }
          return "N/A";
        },
      },
      { field: "Details", valueGetter: (row) => row.Details || "N/A" },
      {
        field: "Linguist",
        valueGetter: (row) => (row.Linguist ? row.Linguist.Name : "N/A"),
      },
      {
        field: "TotalCost",
        valueGetter: (row) => {
          const data = row.TotalCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "AdditionalCost",
        valueGetter: (row) => {
          const data = row.AdditionalCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "LinguistPay",
        valueGetter: (row) => {
          const data = row.LinguistPay;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Profit",
        valueGetter: (row) => {
          const data = row.Profit;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Margin",
        valueGetter: (row) => {
          const data = row.Margin;
          if (data !== undefined && data !== null) {
            const percentage = data * 1;
            if (percentage === 0) return "0%";
            if (percentage === 100) return "100%";
            return `${percentage.toFixed(2)}%`;
          }
          return "N/A";
        },
      },
      {
        field: "Language",
        valueGetter: (row) => (row.Language ? row.Language.Name : "N/A"),
      },
      {
        field: "TargetLanguage",
        valueGetter: (row) => {
          const languages = row.ServiceTargetLanguages;
          if (!languages || languages.length === 0) return "N/A";
          return languages
            .map((item) =>
              item.Language && item.Language.Name ? item.Language.Name : "",
            )
            .filter((name) => name)
            .join(" | ");
        },
      },
      {
        field: "UnitCount",
        valueGetter: (row) => {
          const count = row.UnitCount;

          if (count === null || count === undefined || count === "") {
            return "N/A";
          }

          const unitName = row.UnitType?.Name || "unit";
          const unitLabel =
            Number(count) === 1
              ? unitName.toLowerCase()
              : `${unitName.toLowerCase()}s`;

          return `${Number(count).toLocaleString()} ${unitLabel}`;
        },
      },
      {
        field: "NumberOfLanguages",
        valueGetter: (row) => row.NumberOfLanguages || "N/A",
      },
      {
        field: "Rate",
        valueGetter: (row) => {
          const data = row.Rate;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      { field: "RateType", valueGetter: (row) => row.RateType || "N/A" },
      {
        field: "NotarizationOrCOTCost",
        valueGetter: (row) => row.NotarizationOrCOTCost || "N/A",
      },
      {
        field: "DeliveryType",
        valueGetter: (row) => row.DeliveryType || "N/A",
      },
      {
        field: "DeliveryCost",
        valueGetter: (row) => row.DeliveryCost || "N/A",
      },
      { field: "PaymentType", valueGetter: (row) => row.PaymentType || "N/A" },
      { field: "PaymentFees", valueGetter: (row) => row.PaymentFees || "N/A" },
      {
        field: "DeliveryDate",
        valueGetter: (row) =>
          row.DeliveryDate
            ? dayjs.tz(row.DeliveryDate).format("M/D/YYYY")
            : "N/A",
      },
      { field: "Paid", valueGetter: (row) => row.Paid || "N/A" },
      {
        field: "TranslationType",
        valueGetter: (row) => row.ServiceSubtype?.Name || "N/A",
      },
      {
        field: "Cancelled",
        valueGetter: (row) => {
          const data = row.Cancelled;
          if (data !== undefined && data !== null) {
            return data ? "Yes" : "No";
          }
          return "N/A";
        },
      },
      {
        field: "CancellationReason",
        valueGetter: (row) =>
          row.CancellationReason && row.CancellationReason !== ""
            ? row.CancellationReason
            : "N/A",
      },
      {
        field: "InvoiceTo",
        valueGetter: (row) => (row.Invoicer ? row.Invoicer.Name : "N/A"),
      },
      {
        field: "Invoiced",
        valueGetter: (row) => {
          const data = row.Invoiced;
          if (data !== undefined && data !== null) {
            return data ? "Yes" : "No";
          }
          return "N/A";
        },
      },
      {
        field: "CreatedBy",
        valueGetter: (row) => (row.Creator ? row.Creator.Name : "N/A"),
      },
      {
        field: "CheckedBy",
        valueGetter: (row) => (row.Checker ? row.Checker.Name : "N/A"),
      },
      {
        field: "CheckedOn",
        valueGetter: (row) =>
          row.CheckingDate
            ? dayjs.tz(row.CheckingDate).format("M/D/YYYY")
            : "N/A",
      },
      {
        // 2026-10-13 — Checks is APPENDED at the END: filterData indexes
        // 0/4 (ProjectNumber/Requestor) are consumed positionally by the
        // header renderers, so entries never go mid-array. Single-arg
        // valueGetter: the filterData memo calls filterColumn.valueGetter(row).
        field: "Checks",
        valueGetter: (row) => getChecksEntry(row).bucket,
      },
    ],
    [getChecksEntry],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column, index) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = urlFilteredServices;
      } else {
        filteredData = urlFilteredServices.filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(row);
            return selectedValues.includes(value);
          });
        });
      }

      // Limit Requestor data to top 50 most frequent requestors
      if (column.field === "Requestor" && filteredData.length > 0) {
        const requestorCounts = {};
        filteredData.forEach((row) => {
          const requestor = column.valueGetter(row);
          requestorCounts[requestor] = (requestorCounts[requestor] || 0) + 1;
        });

        const topRequestors = Object.entries(requestorCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 50)
          .map(([requestor]) => requestor);

        return filteredData.filter((row) => {
          const requestor = column.valueGetter(row);
          return topRequestors.includes(requestor);
        });
      }

      return filteredData;
    });
  }, [columnFilters, urlFilteredServices, filterColumns]);

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    localStorage.setItem(
      CONST.TRANSLATION_FILTER_KEY,
      JSON.stringify(newFilterModel),
    );
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    localStorage.setItem(
      CONST.TRANSLATION_SORT_KEY,
      JSON.stringify(newSortModel),
    );
  };

  let totalAccountCharge = 0;
  let totalTotalCost = 0;
  let totalProfit = 0;

  const filteredRowIdsSet = new Set((filterRowIds || []).map(String));
  const includeAll = filteredRowIdsSet.size === 0;

  // Use columnFilteredData for totals calculation to include column filter effects
  const dataForTotals = columnFilteredData || [];

  dataForTotals.forEach((service) => {
    const include = includeAll || filteredRowIdsSet.has(String(service.id));
    if (!include) return;

    totalAccountCharge += Number(service.AccountCharge);
    totalTotalCost += Number(service.TotalCost);
    totalProfit += Number(service.Profit);
  });

  const listOfTotals = [
    { name: "Total Account Charge", total: totalAccountCharge },
    { name: "Total Total Cost", total: totalTotalCost },
    { name: "Total Profit", total: totalProfit },
  ];

  // Count statuses for Members
  let completedCount = 0;
  let deliveredCount = 0;
  let inProgressCount = 0;

  dataForTotals.forEach((service) => {
    const include = includeAll || filteredRowIdsSet.has(String(service.id));
    if (!include) return;

    const status = service.TranslationStatus;
    if (status === "Completed") completedCount++;
    else if (status === "Delivered") deliveredCount++;
    else if (status === "In-Progress") inProgressCount++;
  });

  const listOfServiceStatuses = [
    { name: "Completed", count: completedCount },
    { name: "Delivered", count: deliveredCount },
    { name: "In-Progress", count: inProgressCount },
  ];

  // State for toggling between totals and statuses
  // Check localStorage first, then fall back to role-based default (Admin: totals, Member: statuses)
  const getInitialShowTotals = () => {
    const saved = localStorage.getItem(CONST.TRANSLATIONS_SHOW_TOTALS_KEY);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return currentUser?.role !== "Admin";
  };

  const [showTotals, setShowTotals] = useState(getInitialShowTotals);

  // Save to localStorage when toggling
  const handleToggleShowTotals = () => {
    const newValue = !showTotals;
    setShowTotals(newValue);
    localStorage.setItem(
      CONST.TRANSLATIONS_SHOW_TOTALS_KEY,
      JSON.stringify(newValue),
    );
  };

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters: toolbarHasFilters,
      onClear,
      theme: toolbarTheme,
      selectionEnabled: toolbarSelectionEnabled,
      selectionModel: toolbarSelectionModel,
      allSelectedChecked: toolbarAllSelectedChecked,
      onCancelSelection,
      onMarkChecked,
      cardMode,
      quickFilterValue,
      onQuickFilterChange,
    }) => {
      const isMobileToolbar = isMobile;
      const [anchorEl, setAnchorEl] = useState(null);
      const open = Boolean(anchorEl);

      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      const buttons = [];

      if (toolbarSelectionEnabled) {
        buttons.push({
          label: "Cancel Selection",
          onClick: () => {
            onCancelSelection();
            handleClose();
          },
          icon: null,
        });
        buttons.push({
          label: "Mark As Checked",
          onClick: () => {
            onMarkChecked();
            handleClose();
          },
          icon: <TaskAltRoundedIcon />,
          disabled: toolbarSelectionModel.length === 0,
          isChecked: toolbarAllSelectedChecked,
        });
      }

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {!isMobileToolbar && toolbarSelectionEnabled && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={onCancelSelection}
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Cancel Selection
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TaskAltRoundedIcon />}
                disabled={toolbarSelectionModel.length === 0}
                onClick={onMarkChecked}
                sx={{
                  mr: 1,
                  color: toolbarAllSelectedChecked
                    ? toolbarTheme.palette.text.disabled
                    : toolbarTheme.palette.primary.main,
                  borderColor: toolbarAllSelectedChecked
                    ? toolbarTheme.palette.action.disabled
                    : toolbarTheme.palette.primary.main,
                  "&:hover": toolbarAllSelectedChecked
                    ? {
                        borderColor: toolbarTheme.palette.action.disabled,
                        backgroundColor: "transparent",
                        color: toolbarTheme.palette.text.disabled,
                      }
                    : {
                        borderColor:
                          toolbarTheme.palette.primary.buttonHoverSelected,
                        backgroundColor:
                          toolbarTheme.palette.primary.buttonHoverSelected,
                        color: toolbarTheme.palette.background.default,
                      },
                }}
              >
                Mark As Checked
              </Button>
            </>
          )}

          {isMobileToolbar && buttons.length > 0 && (
            <>
              <Tooltip title="Actions">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{
                    mx: 1,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  <MoreHorizRoundedIcon />
                </IconButton>
              </Tooltip>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ minWidth: 200 }}>
                  {buttons.map((btn, index) => (
                    <MenuItem
                      key={index}
                      onClick={btn.onClick}
                      disabled={btn.disabled}
                      sx={{
                        color: btn.isChecked
                          ? theme.palette.text.disabled
                          : theme.palette.primary.main,
                      }}
                    >
                      {btn.icon && (
                        <ListItemIcon
                          sx={{
                            color: btn.isChecked
                              ? theme.palette.text.disabled
                              : theme.palette.primary.main,
                          }}
                        >
                          {btn.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText>{btn.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Box>
              </Popover>
            </>
          )}

          <Box sx={{ flex: 1 }} />

          {!isMobileToolbar && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {toolbarHasFilters && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobileToolbar && toolbarHasFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "top",
              gap: 1,
            }}
          >
            {cardMode ? (
              <QuickFilterField
                iconOnly
                value={quickFilterValue}
                onChange={onQuickFilterChange}
              />
            ) : (
              <CEQuickFilter iconOnly={isMobileToolbar} />
            )}

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: toolbarTheme.palette.grey.main,
                  borderColor: toolbarTheme.palette.divider,
                  border: `1px solid ${toolbarTheme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: toolbarTheme.palette.primary.main,
                    color: toolbarTheme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setOverlayMode("density");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOverlayMode("columns");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  openExportDialogRef.current?.();
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [theme, isMobile]);

  // Batch C wave 4 — hoisted out of customFooter so the SAME element tree
  // renders in both the desktop grid footer and card mode (mobile.footer).
  const totalsStrip = (
    <Box
      sx={{
        p: "1rem",
        display: "flex",
        flexWrap: "wrap", // wrap to new line if too long
        gap: 2, // spacing between items
        alignItems: "center",
      }}
        >
          <IconButton
            onClick={handleToggleShowTotals}
            size="small"
            sx={{
              color: theme.palette.primary.main,
              "&:hover": {
                color: theme.palette.primary.buttonHoverSelected,
              },
            }}
          >
            <SwapHorizRoundedIcon fontSize="small" />
          </IconButton>

          {showTotals
            ? listOfTotals.map((total) => {
                return (
                  <Box key={`${total}-${total.name}`}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexWrap: "wrap", justifyContent: "center" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {total.name}:
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: theme?.palette?.secondary?.[400],
                          fontWeight: 600,
                        }}
                      >
                        ${Number(total.total).toLocaleString()}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })
            : listOfServiceStatuses.map((status) => {
                return (
                  <Box key={`${status}-${status.name}`}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexWrap: "wrap", justifyContent: "center" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {status.name}:
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: theme?.palette?.secondary?.[400],
                          fontWeight: 600,
                        }}
                      >
                        {status.count}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
    </Box>
  );

  const customFooter = () => {
    return (
      <GridFooterContainer>
        {/* Add what you want here */}

        {totalsStrip}

        <GridFooter
          sx={{
            border: "none", // remove double border
          }}
        />
      </GridFooterContainer>
    );
  };

  // ── Batch C wave 4 — card-mode filter sheet ────────────────────────────────
  // One entry per visible column that carries a header ColumnFilter /
  // ColumnDateFilter, built from the exact same props as those header
  // renderers so filter semantics are identical between modes. In card mode
  // the desktop headers are not rendered, so these sheet copies are the only
  // live filter instances.
  const buildMobileFilterNode = (field) => {
    const colDef = filteredColumns.find((col) => col.field === field);
    // Mirror each desktop header's data source exactly: the cross-filtered
    // arrays feed only ProjectNumber (filterData[0]) and Requestor
    // (filterData[4]); every other header reads the raw list.
    const data =
      field === "ProjectNumber"
        ? filterData[0] || []
        : field === "Requestor"
          ? filterData[4] || []
          : listOfServices || [];
    const FilterComponent = CARD_MODE_DATE_FILTER_FIELDS.includes(field)
      ? ColumnDateFilter
      : ColumnFilter;
    return (
      // The header filters keep their trigger at opacity 0 until the column
      // header is hovered — impossible inside a touch sheet, so the sheet copy
      // forces the trigger visible (the desktop headers are untouched).
      <Box sx={{ "& .MuiIconButton-root": { opacity: 1 } }}>
        <FilterComponent
          data={data}
          field={field}
          valueGetter={colDef?.valueGetter}
          selectedValues={columnFilters[field] || []}
          onFilterChange={(values) =>
            handleColumnFilterChangeWithStorage(field, values)
          }
          // 2026-10-13 — Non-sortable columns (Checks) get no sort UI in
          // the card-mode sheet: ColumnFilter hides the sort block when
          // onSortChange is absent, matching the desktop header.
          onSortChange={
            colDef?.sortable === false
              ? undefined
              : (sortField, direction) => {
                  const newSortModel = direction
                    ? [{ field: sortField, sort: direction }]
                    : [];
                  setSortModel(newSortModel);
                  handleSortModelChange(newSortModel);
                }
          }
          currentSort={sortModel.find((s) => s.field === field)?.sort || null}
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
        />
      </Box>
    );
  };

  const mobileFilterEntries = filteredColumns
    .filter((col) =>
      filterColumns.some((filterCol) => filterCol.field === col.field),
    )
    .map((col) => {
      const selected = columnFilters[col.field] || [];
      return {
        field: col.field,
        headerName: col.headerName || col.field,
        summary: selected.length > 0 ? `${selected.length} selected` : "All",
        active: selected.length > 0,
        node: buildMobileFilterNode(col.field),
      };
    });

  const mobileActiveFilterCount = mobileFilterEntries.filter(
    (entry) => entry.active,
  ).length;

  // Chips row (§3b): one removable chip per active column filter, plus a
  // trailing "Clear all" chip when 2+ filters are active (same semantics as
  // the sheet / toolbar clear). The wrapper adds the search chip itself.
  const mobileFilterChips = mobileFilterEntries
    .filter((entry) => entry.active)
    .map((entry) => ({
      key: `column-filter-${entry.field}`,
      label: `${entry.headerName}: ${columnFilters[entry.field].length} selected`,
      onClear: () => handleColumnFilterChangeWithStorage(entry.field, []),
    }));

  if (mobileFilterChips.length >= 2) {
    mobileFilterChips.push({
      key: "column-filters-clear-all",
      label: "Clear all",
      onClear: () => clearAllFiltersRef.current(),
    });
  }

  // 2026-10-12 (Amine) — Derived values for the mini profile side panel
  // (desktop single-click). Formatting mirrors the grid column valueGetters
  // so the panel never disagrees with the row it was opened from.
  const sidePanelValues = useMemo(() => {
    if (!sidePanelRow) return null;

    const formatDate = (data) =>
      data && data !== "" ? dayjs.tz(data).format("M/D/YYYY") : "N/A";

    const formatMoney = (data, useLocale = false) => {
      if (data === undefined || data === null || data === "") return "N/A";
      if (parseFloat(data) === 0) return "N/A";
      return useLocale ? `$${parseFloat(data).toLocaleString()}` : `$${data}`;
    };

    const formatMargin = (data) => {
      if (data === undefined || data === null) return "N/A";
      const percentage = data * 1;
      if (percentage === 0) return "0%";
      if (percentage === 100) return "100%";
      return `${percentage.toFixed(2)}%`;
    };

    // Units column parity: "<count> <unitType>" with singular/plural casing.
    const formatUnits = () => {
      const count = sidePanelRow.UnitCount;
      if (count === null || count === undefined || count === "") return "N/A";
      const unitName = sidePanelRow.UnitType?.Name || "unit";
      const unitLabel =
        Number(count) === 1
          ? unitName.toLowerCase()
          : `${unitName.toLowerCase()}s`;
      return `${Number(count).toLocaleString()} ${unitLabel}`;
    };

    const targetLanguageNames = (sidePanelRow.ServiceTargetLanguages || [])
      .map((item) => (item.Language && item.Language.Name) || "")
      .filter(Boolean)
      .join(" | ");

    const contacts = (
      Array.isArray(sidePanelRow.ContactPersons)
        ? sidePanelRow.ContactPersons
        : []
    )
      .map((contact, index) => ({
        key: contact?.id ?? `contact-${index}`,
        name: contact?.Name || "",
        phone: normalizePhoneNumber(contact?.PhoneNumber),
        email: (contact?.Email || "").trim() || null,
      }))
      .filter((contact) => contact.name || contact.phone !== "N/A");

    const emailThreadIds = parseEmailThreadIds(sidePanelRow.EmailThreadID);

    // 2026-10-12 — unified category-aware engine for the Translation
    // category: code-backed built-ins (per-issue scope filtered), converted
    // condition-backed built-ins and Settings custom checks, one merged
    // list. translationChecksConfigured (config-level oracle) gates the
    // evaluation so a truly-unconfigured list shows the neutral empty
    // state instead of an empty "all passed" claim.
    const checksConfigured = translationChecksConfigured;
    const checks = checksConfigured
      ? runServiceChecks(sidePanelRow, "Translation", {
          mileageRates,
          linguistsById,
          checkConfig,
        })
      : [];

    // 2026-10-12 (Amine) — quick-action data. Phones are normalized here (the
    // single formatting source); the render layer only decides which buttons
    // to show. PhoneNumber is a VIRTUAL on Linguist/Requestor composed from
    // the PhoneNumbers association, so prefer the association rows when the
    // payload carries them, then the flat PhoneNumber field. Returns null
    // when nothing usable exists so no dead action buttons render.
    const resolveRecordPhone = (record) => {
      if (!record) return null;
      const phones = Array.isArray(record.PhoneNumbers)
        ? record.PhoneNumbers.filter((p) => String(p?.Number || "").trim())
        : [];
      if (phones.length > 0) {
        const primary = phones.find((p) => p.IsPrimary) || phones[0];
        const withPrefix =
          String(primary?.CountryCode || "") &&
          String(primary.CountryCode) !== "1"
            ? `+${primary.CountryCode}${primary.Number}`
            : primary.Number;
        const normalized = normalizePhoneNumber(withPrefix);
        return normalized !== "N/A" ? normalized : null;
      }
      const normalized = normalizePhoneNumber(record.PhoneNumber);
      return normalized !== "N/A" ? normalized : null;
    };

    const linguistRecord = sidePanelRow.Linguist || null;
    const linguistBrief =
      linguistRecord &&
      linguistRecord.id !== null &&
      linguistRecord.id !== undefined
        ? linguistsById[linguistRecord.id]
        : null;
    // "N/A" linguist rows (canonical id 1) must never show quick actions.
    const linguistNotApplicable =
      Number(linguistRecord?.id) === 1 ||
      String(linguistRecord?.Name || "")
        .trim()
        .toUpperCase() === "N/A";
    // The list payload's linguist include may not load PhoneNumbers, so the
    // /linguists/brief record (which does) is the fallback phone source.
    const linguistPhone =
      resolveRecordPhone(linguistRecord) || resolveRecordPhone(linguistBrief);
    const linguistEmail =
      (linguistRecord?.Email || "").trim() ||
      (linguistBrief?.Email || "").trim() ||
      null;

    const requestorPhone = resolveRecordPhone(sidePanelRow.Requestor);
    const invoicerPhone = resolveRecordPhone(sidePanelRow.Invoicer);

    // 2026-10-12 (Amine) — copy / Open in Maps actions on the Address row.
    // The row only renders for a real value (not "", null or "N/A") — the
    // same no-dead-button guard style as the contact actions above.
    const rawAddress =
      typeof sidePanelRow.Address === "string"
        ? sidePanelRow.Address.trim()
        : "";
    const hasAddress =
      rawAddress !== "" && rawAddress.toUpperCase() !== "N/A";

    return {
      projectNumber:
        sidePanelRow.ProjectNumber && sidePanelRow.ProjectNumber !== ""
          ? sidePanelRow.ProjectNumber
          : "N/A",
      status: sidePanelRow.TranslationStatus || "N/A",
      // Schedule & Delivery — grid valueGetter parity.
      date: formatDate(sidePanelRow.Date),
      deadline: formatDate(sidePanelRow.Deadline),
      deliveryDate: formatDate(sidePanelRow.DeliveryDate),
      // Client Data.
      client: sidePanelRow.Client?.Account || "N/A",
      requestor: sidePanelRow.Requestor?.Name || "N/A",
      invoicer: sidePanelRow.Invoicer?.Name || "N/A",
      translationType:
        sidePanelRow.ServiceSubtype?.Name &&
        sidePanelRow.ServiceSubtype.Name !== ""
          ? sidePanelRow.ServiceSubtype.Name
          : "N/A",
      sourceLanguage: sidePanelRow.Language?.Name || "N/A",
      targetLanguages: targetLanguageNames || "N/A",
      linguist: sidePanelRow.Linguist?.Name || "N/A",
      address: hasAddress ? rawAddress : "N/A",
      addressRaw: hasAddress ? rawAddress : null,
      hasAddress,
      // Money — AccountCharge is locale-formatted in the grid, the rest keep
      // the DB string form (`$${data}`), same as their valueGetters.
      accountCharge: formatMoney(sidePanelRow.AccountCharge, true),
      totalCost: formatMoney(sidePanelRow.TotalCost),
      additionalCost: formatMoney(sidePanelRow.AdditionalCost),
      notCotCost: sidePanelRow.NotarizationOrCOTCost || "N/A",
      deliveryCost: sidePanelRow.DeliveryCost || "N/A",
      paymentFees: sidePanelRow.PaymentFees || "N/A",
      linguistPay: formatMoney(sidePanelRow.LinguistPay),
      profit: formatMoney(sidePanelRow.Profit),
      margin: formatMargin(sidePanelRow.Margin),
      // Rates & Units.
      units: formatUnits(),
      numberOfLanguages: sidePanelRow.NumberOfLanguages || "N/A",
      rate: formatMoney(sidePanelRow.Rate),
      rateType: sidePanelRow.RateType || "N/A",
      // Delivery & Payment.
      deliveryType: sidePanelRow.DeliveryType || "N/A",
      paymentType: sidePanelRow.PaymentType || "N/A",
      // Status & Record. Paid is a BOOLEAN in the DB and the grid's raw
      // passthrough renders blank when true (React ignores booleans), so the
      // panel uses the same Yes/No/N/A triad as its sibling boolean columns
      // (Cancelled / Invoiced) — the house convention for booleans.
      paid:
        sidePanelRow.Paid === undefined || sidePanelRow.Paid === null
          ? "N/A"
          : sidePanelRow.Paid
            ? "Yes"
            : "No",
      cancelled:
        sidePanelRow.Cancelled === undefined ||
        sidePanelRow.Cancelled === null
          ? "N/A"
          : sidePanelRow.Cancelled
            ? "Yes"
            : "No",
      invoiced:
        sidePanelRow.Invoiced === undefined || sidePanelRow.Invoiced === null
          ? "N/A"
          : sidePanelRow.Invoiced
            ? "Yes"
            : "No",
      cancellationReason:
        sidePanelRow.CancellationReason &&
        sidePanelRow.CancellationReason !== ""
          ? sidePanelRow.CancellationReason
          : "N/A",
      createdBy: sidePanelRow.Creator ? sidePanelRow.Creator.Name : "N/A",
      checkedBy: sidePanelRow.Checker ? sidePanelRow.Checker.Name : "N/A",
      checkedOn: formatDate(sidePanelRow.CheckingDate),
      details: sidePanelRow.Details || "N/A",
      // Quick-action data — render shows buttons only for what exists.
      linguistProfileId: linguistNotApplicable
        ? null
        : linguistRecord?.id || null,
      linguistPhone: linguistNotApplicable ? null : linguistPhone,
      linguistEmail: linguistNotApplicable ? null : linguistEmail,
      clientProfileId: sidePanelRow.Client?.id || null,
      requestorPhone,
      requestorEmail: (sidePanelRow.Requestor?.Email || "").trim() || null,
      invoicerPhone,
      invoicerEmail: (sidePanelRow.Invoicer?.Email || "").trim() || null,
      contacts,
      emailThreadIds,
      checks,
      checksConfigured,
    };
  }, [
    sidePanelRow,
    mileageRates,
    linguistsById,
    checkConfig,
    translationChecksConfigured,
  ]);

  // 2026-10-12 — Profit tint for the side panel Costs section (display-only;
  // the formatted sidePanelValues string carries no sign information).
  const sidePanelProfitNumber =
    sidePanelRow &&
    sidePanelRow.Profit !== null &&
    sidePanelRow.Profit !== undefined &&
    sidePanelRow.Profit !== ""
      ? Number(sidePanelRow.Profit)
      : NaN;
  const sidePanelProfitColor =
    !Number.isFinite(sidePanelProfitNumber) || sidePanelProfitNumber === 0
      ? theme.palette.text.secondary
      : sidePanelProfitNumber > 0
        ? theme.palette.success.main
        : theme.palette.error.main;

  // Overlay for Density & Columns
  const GridOptionsOverlay = () => (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.alt,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        width: { xs: "calc(100vw - 2rem)", sm: "700px", md: "860px" },
        maxWidth: "calc(100vw - 2rem)",
        minWidth: 0,
      }}
    >
      {overlayMode === "density" && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOverlayOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      )}

      {overlayMode === "columns" && (
        <Box>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Columns
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setColumnsVisible({})}
              disabled={
                !columns.some((col) => columnsVisible[col.field] === false)
              }
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                px: 1,
              }}
            >
              Reset
            </Button>
          </Box>
          <Grid container spacing={0.5} rowSpacing={1.25}>
            {columns.map((col) => (
              <Grid key={col.field} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  label={col.headerName}
                  sx={{
                    m: 0,
                    width: "100%",
                    py: 0.25,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      lineHeight: 1.3,
                      display: "inline-flex",
                      alignItems: "center",
                    },
                    "& .MuiCheckbox-root": {
                      py: 0.5,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnsVisible[col.field] !== false}
                      onChange={(e) => {
                        setColumnsVisible((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }));
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

    </Box>
  );

  // Export Dialog Content Component
  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No translations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services

              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              const csvRows = dataToExport
                .filter((row) => {
                  if (!row || typeof row !== "object") {
                    console.warn("Skipping invalid row:", row);
                    return false;
                  }
                  return true;
                })
                .map((row, rowIndex) => {
                  try {
                    return visibleColumns
                      .map((col) => {
                        try {
                          let value = "";
                          if (col.field) {
                            // Check if this field has a special mapping
                            const mappedPath = fieldPaths[col.field];
                            if (mappedPath) {
                              value = getFieldValue(row, mappedPath);
                            } else if (col.field === "TargetLanguage") {
                              // Special handling for TargetLanguage (complex nested array)
                              const languages = row.ServiceTargetLanguages;
                              if (languages && Array.isArray(languages)) {
                                value = languages
                                  .map((tl) => tl.Language?.Name || "")
                                  .join(" | ");
                              }
                            } else if (col.field === "Checks") {
                              // 2026-10-13 — No raw row field; mirror the
                              // column's valueGetter and export the check
                              // summary ("OK" / "N issue(s)") so the export
                              // is never blank for Checks.
                              const { issues } = getChecksEntry(row);
                              value =
                                issues.length === 0
                                  ? "OK"
                                  : `${issues.length} issue(s)`;
                            } else {
                              value = getFieldValue(row, col.field);
                              if (value === "" && col.field in row) {
                                value = row[col.field];
                              }
                            }
                          }
                          const strValue = String(value || "").replace(
                            /"/g,
                            '""',
                          );
                          return `"${strValue}"`;
                        } catch (cellError) {
                          console.warn(
                            `Error processing cell at row ${rowIndex}, column ${col.field}:`,
                            cellError.message,
                          );
                          return '""';
                        }
                      })
                      .join(",");
                  } catch (rowError) {
                    console.warn(
                      `Error processing row ${rowIndex}:`,
                      rowError.message,
                    );
                    return "";
                  }
                })
                .filter((row) => row.length > 0);

              const csvContent = [csvHeader, ...csvRows].join("\n");

              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `translations-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={async () => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No translations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services
              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              showSnackbar("Preparing XLSX export…", theme, "info");

              // Create workbook and worksheet
              const ExcelJS = await loadExcelJS();
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Translations");

              // Add header row with formatting
              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              // Track column widths
              const columnWidths = new Array(visibleColumns.length).fill(15);

              // Add data rows and calculate widths
              dataToExport.forEach((row) => {
                if (!row || typeof row !== "object") return;

                const rowData = visibleColumns.map((col, colIndex) => {
                  try {
                    let value = "";
                    if (col.field) {
                      const mappedPath = fieldPaths[col.field];
                      if (mappedPath) {
                        value = getFieldValue(row, mappedPath);
                      } else if (col.field === "TargetLanguage") {
                        const languages = row.ServiceTargetLanguages;
                        if (languages && Array.isArray(languages)) {
                          value = languages
                            .map((tl) => tl.Language?.Name || "")
                            .join(" | ");
                        }
                      } else if (col.field === "Checks") {
                        // 2026-10-13 — mirror the column's valueGetter.
                        const { issues } = getChecksEntry(row);
                        value =
                          issues.length === 0
                            ? "OK"
                            : `${issues.length} issue(s)`;
                      } else {
                        value = getFieldValue(row, col.field);
                        if (value === "" && col.field in row) {
                          value = row[col.field];
                        }
                      }
                    }
                    const strValue = String(value || "");
                    // Update column width based on content
                    columnWidths[colIndex] = Math.max(
                      columnWidths[colIndex],
                      Math.min(strValue.length + 2, 50),
                    );
                    return strValue;
                  } catch (error) {
                    console.warn(`Error processing cell:`, error.message);
                    return "";
                  }
                });
                worksheet.addRow(rowData);
              });

              // Apply calculated column widths
              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });

              // Generate and download file
              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `translations-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
              showSnackbar(
                "Couldn't prepare the XLSX export — check your connection and try again.",
                theme,
                "error",
              );
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  // Export dialog bridge — keep the dialog factory fresh at click time. The
  // export MenuItem lives inside a memoized toolbar, so it captures
  // <ExportDialogContent /> from the render where that memo last recomputed —
  // which can predate the data load (stale closure → the dialog says
  // "No data available to export"). This ref is reassigned every commit, so
  // the click always opens the latest render's dialog with current data.
  const openExportDialogRef = useRef(null);
  useEffect(() => {
    openExportDialogRef.current = () =>
      openDialog({
        title: "Export Data",
        theme,
        customContent: <ExportDialogContent />,
      });
  });

  return (
    <Box
      //borderRadius="1.5rem 1.5rem 0 0"
      p="1rem 1rem 0 1rem"
      //m=".5rem 1.5rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`, // Take full viewport height minus margin
        // 100dvh where supported: bottom controls stay clear of iOS Safari's collapsing chrome
        "@supports (height: 100dvh)": {
          height: `calc(100dvh - ${NAVBAR_HEIGHT + (isMobile ? 0 : 20)}px)`,
        },
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header section */}
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="space-between"
            sx={{ px: 1 }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              gap={2}
            >
              <Box
                sx={{
                  flex: isMobile ? 0 : 1,
                  maxWidth: isMobile ? undefined : FILTER_SELECTOR_WIDTH,
                }}
              >
                <CustomDateSelector
                  label="Date Range"
                  months={months}
                  years={
                    yearOptions.length > 0 ? yearOptions : [String(currentYear)]
                  }
                  selectedMonths={tempSelectedMonths}
                  selectedYears={tempSelectedYear}
                  onSave={handleDateSelectionSave}
                  isMultiMonth={true}
                  isMultiYear={false}
                  monthGridItemSize={3}
                  yearGridItemSize={4}
                  showClearFilter={false}
                  iconOnly={isMobile}
                />
              </Box>

              <Box display="flex" gap={2} alignItems="center">
                {!isMobile ? (
                  <Button
                    onClick={() => {
                      setOverlayName(OVERLAY_NAMES.TRANSLATION_DOCUMENTS);
                    }}
                    startIcon={<DescriptionRoundedIcon />}
                    sx={{
                      width: "200px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      padding: "8px 16px",
                      backgroundColor: theme.palette.background.default,
                      borderRadius: "12px",
                      boxShadow:
                        "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                      color: theme.palette.primary.main,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      "& .MuiButton-startIcon": {
                        marginRight: 0,
                      },
                      "& .MuiButton-endIcon": {
                        display: "none",
                      },
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                        boxShadow:
                          "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, textAlign: "center" }}>Documents</Box>
                  </Button>
                ) : (
                  <Tooltip title="Documents">
                    <IconButton
                      onClick={() => {
                        setOverlayName(OVERLAY_NAMES.TRANSLATION_DOCUMENTS);
                      }}
                      sx={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: theme.palette.background.default,
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        borderRadius: "12px",
                        border: "none",
                        color: theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: theme.palette.primary.buttonHover,
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <DescriptionRoundedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Button
                  onClick={() => {
                    setPendingCreateNew(true);
                  }}
                  startIcon={!isMobile && <AddRoundedIcon />}
                  sx={{
                    width: isMobile ? "40px" : "200px",
                    minWidth: isMobile ? "40px" : "200px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: isMobile ? 0 : "8px 16px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      margin: isMobile ? 0 : undefined,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  {isMobile ? <AddRoundedIcon /> : <Box sx={{ flex: 1, textAlign: "center" }}>Add Translation</Box>}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* 2026-10-30 (Amine): the standing "Showing N assignments from the
          deadline alert" bar is GONE — the same message is now a one-shot
          snackbar (see the effect above `hasAnyActiveFilters`), so nothing sits
          above the grid permanently. The whole block is parked below, including
          the bespoke "Clear filter" button, because the grid's own Clear Filters
          button clears the URL filter too. Re-enable only if the standing notice
          is ever wanted back.

      {urlFilterActive && (
        <Box
          sx={{
            mx: 1,
            mt: 2,
            px: 2,
            py: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexShrink: 0,
            borderRadius: "12px",
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[1],
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary }}
          >
            {`Showing ${(columnFilteredData || []).length} assignment${
              (columnFilteredData || []).length === 1 ? "" : "s"
            } from the deadline alert`}
          </Typography>
          <Button
            size="small"
            onClick={handleClearAllFilters}
            sx={{
              textTransform: "none",
              fontSize: "0.875rem",
              borderRadius: "12px",
              color: theme.palette.primary.main,
              whiteSpace: "nowrap",
            }}
          >
            Clear filter
          </Button>
        </Box>
      )}
      */}

      {/* DataGrid section */}
      <Box
        ref={sidePanelGridRef}
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          position: "relative",
          boxShadow: theme.shadows[3],
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          // The deadline-alert notice used to sit above the grid and shave this
          // margin down. It is a snackbar now, so the gap is constant.
          mt: 2,
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        <DataGridResponsive
          mobile={{
            // Card mode (mobile) config — the desktop grid ignores this prop.
            primaryField: "ProjectNumber",
            badgeField: "TranslationStatus",
            hiddenFields: [],
            multilineFields: ["Details", "TargetLanguage", "CancellationReason"],
            pageSize: 20,
            selectable: true,
            // 2026-10-05 (Amine): mobile has no right-click — the card's
            // top-right ⋮ replays this screen's context menu (same handler,
            // same items and disabled states).
            contextMenuButton: true,
            // Batch C wave 4 — card-mode filter sheet: entries reuse the exact
            // header ColumnFilter / ColumnDateFilter props, activeCount drives
            // the toolbar badge, and Clear all reuses the existing toolbar
            // clear (column filters + quick filter, design §3b).
            filters: mobileFilterEntries,
            activeCount: mobileActiveFilterCount,
            onClearFilters: () => clearAllFiltersRef.current(),
            // Totals / statuses strip: the same element tree as the desktop
            // grid footer (hoisted above into `totalsStrip`).
            footer: totalsStrip,
            // Removable chips under the control row (wrapper adds the search
            // chip itself).
            chips: mobileFilterChips,
          }}
          localeText={{
            // With a quick range on, "nothing added yet for this month" would be
            // a lie — the grid's own no-rows state stays, only the wording
            // follows the range.
            noRowsLabel: "No translation projects added yet for this month.",
            noResultsOverlayLabel: "No matches for your filters",
          }}
          apiRef={apiRef}
          density={density}
          loading={isLoading}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          checkboxSelection={selectionEnabled}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          onStateChange={(e) => {
            const lookup = e.filter?.filteredRowsLookup;
            if (!lookup) return;

            const nextIds = Object.keys(lookup).filter((key) => lookup[key]);
            nextIds.sort();

            const prevIds = prevFilteredRowIdsRef.current;
            const isSame =
              prevIds.length === nextIds.length &&
              prevIds.every((id, idx) => id === nextIds[idx]);

            if (!isSame) {
              prevFilteredRowIdsRef.current = nextIds;
              setFilterRowIds(nextIds);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          columns={filteredColumns}
          disableColumnMenu
          disableColumnSorting
          //rowSelectionModel={openFilter ? [] : undefined}
          disableRowSelectionOnClick={!!openFilter}
          onRowClick={(params, event) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            // A long-press opens a 700ms suppression WINDOW synchronously,
            // while the `selectionEnabled` state update may not have rendered
            // yet; the click that follows the long-press must neither toggle
            // selection nor open the translation. Outside the window the
            // gate is structurally closed (round 7: boolean → timestamp +
            // single-shot reset) — a stale guard can never eat a plain tap.
            if (Date.now() < suppressClickUntilRef.current) {
              event.defaultMuiPrevented = true;
              suppressClickUntilRef.current = 0;
              return;
            }

            if (selectionEnabled) {
              // Armed: the wrapper's auto-toggle handles this tap's selection.
              return;
            }

            // Blanket card rule 2026-10-05 (Amine) — one tap opens the
            // translation exactly like the desktop double-click (desktop keeps
            // click = highlight, double-click = open).
            if (isMobile) {
              const data = {
                Translation: params.row,
                TranslationID: params.row.id,

                Clients: listOfClients,
                Linguists: listOfLinguists,
                Languages: listOfLanguages,
                Translations: listOfServices,
                TranslationStatuses: listOfStatuses,
                TranslationTypes: listOfTypes,
                ProjectNumber: latestProjectNumber,
                RateTypes: rateTypes,

                ComingFrom: `/translations`,
              };

              navigate(`/translations/${params.row.id}`, { state: data });
              return;
            }

            setSelectionModel([params.id]);
            // 2026-10-12 (Amine) — the panel footer's Edit and ⋮ actions
            // reuse the context-menu handlers, which read `selectedRow`;
            // keep it in lockstep with the row the panel shows.
            setSelectedRow(params.id);
            // 2026-10-12 (Amine) — desktop single-click also opens the mini
            // profile side panel for the tapped row; clicking another row
            // swaps the panel content. Mobile tap-through above is unchanged.
            setSidePanelRowId(params.id);
          }}
          onRowDoubleClick={(params) => {
            if (openFilter || selectionEnabled) return;
            const data = {
              Translation: params.row,
              TranslationID: params.row.id,

              Clients: listOfClients,
              Linguists: listOfLinguists,
              Languages: listOfLanguages,
              Translations: listOfServices,
              TranslationStatuses: listOfStatuses,
              TranslationTypes: listOfTypes,
              ProjectNumber: latestProjectNumber,
              RateTypes: rateTypes,

              ComingFrom: `/translations`,
            };

            navigate(`/translations/${params.row.id}`, { state: data });
          }}
          getCellClassName={(params) => {
            return params.value === "No" ||
              params.value === "N/A" ||
              params.value === "0%" ||
              params.value === "null"
              ? "notAvailable"
              : "";
          }}
          slots={{ toolbar: CustomToolbar, footer: customFooter }}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
              onMouseDown: handleRowMouseDown,
              onMouseUp: handleRowMouseUp,
              onMouseLeave: handleRowMouseUp,
              onTouchStart: handleRowTouchStart,
              onTouchEnd: handleRowTouchEnd,
              onTouchCancel: handleRowTouchEnd,
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              hasAnyActiveFilters,
              onClear: () => clearAllFiltersRef.current(),
              theme,
              selectionEnabled,
              selectionModel,
              allSelectedChecked: selectionStats.allChecked,
              onCancelSelection: handleCancelSelection,
              onMarkChecked: handleMarkSelectedAsChecked,
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",
            height: "100%", // Make DataGrid take full height of container
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-sortIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
        />

        {/* 2026-10-12 (Amine) — Mini profile side panel (desktop
            single-click), ported from the Interpretations list: docked
            inside the grid wrapper, resizable from its left edge, and
            never rendered on mobile (hard guard in the condition). */}
        {!isMobile && sidePanelRow && sidePanelValues && (
          <Box
            ref={sidePanelRef}
            role="complementary"
            aria-label="Translation quick view"
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              bottom: 0,
              width: effectiveSidePanelWidth,
              zIndex: 10,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderLeft: `1px solid ${theme.palette.divider}`,
              borderRadius: "0 12px 12px 0",
              bgcolor: theme.palette.background.alt,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "-8px 0 24px rgba(0,0,0,0.45)"
                  : "-8px 0 24px rgba(0,0,0,0.08)",
            }}
          >
            {/* Drag handle on the panel's left edge — drag left/right to
                resize; double-click resets to the responsive default. */}
            <Box
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize panel — drag to resize, double-click to reset"
              title="Drag to resize — double-click to reset"
              onPointerDown={handleSidePanelResizeStart}
              onDoubleClick={() => {
                // 2026-10-12 (Amine) — drops the manual override so the
                // width snaps back to the breakpoint-responsive default.
                setSidePanelWidth(null);
                localStorage.removeItem(SIDE_PANEL_WIDTH_STORAGE_KEY);
              }}
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 8,
                zIndex: 2,
                cursor: "col-resize",
                touchAction: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "& .sidePanelResizePill": {
                  width: 3,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: "divider",
                  transition: "background-color 120ms ease",
                },
                "&:hover .sidePanelResizePill": {
                  bgcolor: theme.palette.primary.main,
                },
                ...(isSidePanelResizing && {
                  "& .sidePanelResizePill": {
                    bgcolor: theme.palette.primary.main,
                  },
                }),
              }}
            >
              <Box className="sidePanelResizePill" />
            </Box>

            {/* Status accent strip */}
            <Box
              sx={{
                height: 3,
                flexShrink: 0,
                bgcolor: getStatusColor(theme, sidePanelValues.status),
              }}
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{
                px: 2,
                pl: 2.5,
                py: 1.25,
                flexShrink: 0,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ minWidth: 0 }}
              >
                <Typography
                  noWrap
                  sx={{ fontSize: "1.05rem", fontWeight: 700 }}
                >
                  {sidePanelValues.projectNumber}
                </Typography>
                <Box
                  sx={{
                    borderRadius: "12px",
                    px: "0.625rem",
                    py: "0.2rem",
                    backgroundColor: getStatusColor(
                      theme,
                      sidePanelValues.status,
                    ),
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    maxWidth: 170,
                  }}
                >
                  <Typography
                    sx={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: theme.palette.background.default,
                      fontSize: "0.78rem",
                      fontWeight: 600,
                    }}
                  >
                    {sidePanelValues.status}
                  </Typography>
                </Box>
              </Stack>
              <IconButton
                size="small"
                aria-label="Close side panel"
                onClick={() => setSidePanelRowId(null)}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Stack>

            {/* 2026-10-12 (Amine) — Checks sit at the very top of the panel
                (above the money strip) so issues are always visible without
                scrolling. Zero configured checks for the Translation
                category → neutral setup hint; configured and silent → the
                all-passed box; otherwise the failing checks. */}
            <Box sx={{ flexShrink: 0 }}>
              <SidePanelSection
                title="Checks"
                icon={<FactCheckRoundedIcon sx={{ fontSize: 16 }} />}
              >
                {!sidePanelValues.checksConfigured ? (
                  <Typography
                    sx={{ color: "text.secondary", fontSize: "0.875rem" }}
                  >
                    No checks are set up for Translations yet — create them in
                    Settings → Validation Checks.
                  </Typography>
                ) : sidePanelValues.checks.length === 0 ? (
                  <Box
                    sx={{
                      borderRadius: "10px",
                      px: 1.25,
                      py: 1,
                      bgcolor: (theme) => `${theme.palette.success.main}14`,
                    }}
                  >
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <ChecksSeverityBadge severity="passed" />
                      <Typography
                        sx={{ fontSize: "0.875rem", fontWeight: 500 }}
                      >
                        All checks passed
                      </Typography>
                    </Stack>
                  </Box>
                ) : (
                  sidePanelValues.checks.map((issue, index) => (
                    <Stack
                      key={`${issue.code}-${index}`}
                      direction="row"
                      spacing={0.75}
                      alignItems="flex-start"
                      sx={{ py: 0.5 }}
                    >
                      {/* Chip language (Amine 2026-10-13): mini severity
                          badges matching the Checks column chip. */}
                      {issue.severity === "error" ? (
                        <ChecksSeverityBadge
                          severity="error"
                          sx={{ mt: "2px" }}
                        />
                      ) : (
                        <ChecksSeverityBadge
                          severity="warning"
                          sx={{ mt: "2px" }}
                        />
                      )}
                      <Typography
                        sx={{
                          minWidth: 0,
                          overflowWrap: "anywhere",
                          fontSize: "0.875rem",
                        }}
                      >
                        {issue.message}
                      </Typography>
                    </Stack>
                  ))
                )}
              </SidePanelSection>
              <Divider sx={{ my: 0.5 }} />
            </Box>

            {/* Money stat strip — quick-glance totals (sidePanelValues only) */}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                px: 2,
                pl: 2.5,
                py: 1.25,
                flexShrink: 0,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              {[
                {
                  label: "Account Charge",
                  value: sidePanelValues.accountCharge,
                  color: theme.palette.secondary.main,
                },
                {
                  label: "Total Cost",
                  value: sidePanelValues.totalCost,
                  color: theme.palette.primary.main,
                },
                {
                  label: "Linguist Pay",
                  value: sidePanelValues.linguistPay,
                  color: theme.palette.text.primary,
                },
              ].map((stat) => (
                <Box
                  key={stat.label}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: "10px",
                    bgcolor: `${theme.palette.primary.main}0D`,
                    px: 1,
                    py: 0.75,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.03em",
                      lineHeight: 1.25,
                      color: "text.secondary",
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      color: stat.color,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {stat.value}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
              <SidePanelSection
                title="Schedule & Delivery"
                icon={<AccessTimeRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelFieldGrid>
                  <SidePanelCompactField
                    label="Date"
                    value={sidePanelValues.date}
                  />
                  <SidePanelCompactField
                    label="Deadline"
                    value={sidePanelValues.deadline}
                  />
                  <SidePanelGridFullRow>
                    <SidePanelCompactField
                      label="Delivery Date"
                      value={sidePanelValues.deliveryDate}
                    />
                  </SidePanelGridFullRow>
                </SidePanelFieldGrid>
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              <SidePanelSection
                title="Client Data"
                icon={<PersonRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelFieldGrid>
                  <SidePanelCompactField
                    label="Client"
                    value={sidePanelValues.client}
                    actions={
                      sidePanelValues.clientProfileId
                        ? [
                            {
                              key: "open",
                              label: "Open Client Profile",
                              Icon: OpenInNewRoundedIcon,
                              onClick: () =>
                                navigate(
                                  `/clients/${sidePanelValues.clientProfileId}`,
                                ),
                            },
                          ]
                        : []
                    }
                  />
                  <SidePanelCompactField
                    label="Requestor"
                    value={sidePanelValues.requestor}
                    actions={buildDirectContactActions(
                      sidePanelValues.requestorPhone,
                      sidePanelValues.requestorEmail,
                    )}
                  />
                  <SidePanelCompactField
                    label="Invoice To"
                    value={sidePanelValues.invoicer}
                    actions={buildDirectContactActions(
                      sidePanelValues.invoicerPhone,
                      sidePanelValues.invoicerEmail,
                    )}
                  />
                  <SidePanelCompactField
                    label="Type"
                    value={sidePanelValues.translationType}
                  />
                  <SidePanelCompactField
                    label="Source Language"
                    value={sidePanelValues.sourceLanguage}
                  />
                  <SidePanelCompactField
                    label="Target Languages"
                    value={sidePanelValues.targetLanguages}
                  />
                </SidePanelFieldGrid>
                {/* 2026-10-12 (Amine) — Linguist and Address stay
                    single-column full-width: both can be long. Linguist
                    actions (profile / call / text / email) are all
                    suppressed when the linguist is N/A. */}
                <SidePanelField
                  label="Linguist"
                  value={sidePanelValues.linguist}
                  actions={[
                    ...(sidePanelValues.linguistProfileId
                      ? [
                          {
                            key: "open",
                            label: "Open Linguist Profile",
                            Icon: OpenInNewRoundedIcon,
                            onClick: () =>
                              navigate(
                                `/linguists/${sidePanelValues.linguistProfileId}`,
                              ),
                          },
                        ]
                      : []),
                    ...buildDirectContactActions(
                      sidePanelValues.linguistPhone,
                      sidePanelValues.linguistEmail,
                    ),
                  ]}
                />
                {/* 2026-10-12 (Amine) — Copy + Open in Maps actions; the row
                    renders only for a real address value so no dead buttons
                    or misleading "N/A" copy sources ever appear. */}
                {sidePanelValues.hasAddress && (
                  <SidePanelField
                    label="Address"
                    value={sidePanelValues.address}
                    multiline
                    actions={[
                      {
                        key: "copy",
                        label: "Copy address",
                        Icon: ContentCopyRoundedIcon,
                        onClick: () =>
                          copyAddressToClipboard(
                            sidePanelValues.addressRaw,
                            showSnackbar,
                            theme,
                          ),
                      },
                      {
                        key: "maps",
                        label: "Open in Maps",
                        Icon: PlaceRoundedIcon,
                        onClick: () =>
                          window.open(
                            buildMapsSearchUrl(sidePanelValues.addressRaw),
                            "_blank",
                            "noopener,noreferrer",
                          ),
                      },
                    ]}
                  />
                )}
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              <SidePanelSection
                title="Costs"
                icon={<AttachMoneyRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelFieldGrid>
                  <SidePanelCompactField
                    label="Account Charge"
                    value={sidePanelValues.accountCharge}
                  />
                  <SidePanelCompactField
                    label="Total Cost"
                    value={sidePanelValues.totalCost}
                  />
                  <SidePanelCompactField
                    label="Additional Cost"
                    value={sidePanelValues.additionalCost}
                  />
                  <SidePanelCompactField
                    label="Not/COT Cost"
                    value={sidePanelValues.notCotCost}
                  />
                  <SidePanelCompactField
                    label="Delivery Cost"
                    value={sidePanelValues.deliveryCost}
                  />
                  <SidePanelCompactField
                    label="Payment Fees"
                    value={sidePanelValues.paymentFees}
                  />
                  <SidePanelCompactField
                    label="Linguist Pay"
                    value={sidePanelValues.linguistPay}
                  />
                  <SidePanelCompactField
                    label="Profit"
                    value={
                      <Typography
                        sx={{
                          fontSize: "0.9375rem",
                          fontWeight: 600,
                          color: sidePanelProfitColor,
                        }}
                      >
                        {sidePanelValues.profit}
                      </Typography>
                    }
                  />
                  <SidePanelCompactField
                    label="Margin"
                    value={sidePanelValues.margin}
                  />
                </SidePanelFieldGrid>
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              <SidePanelSection
                title="Rates & Units"
                icon={<AttachMoneyRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelFieldGrid>
                  <SidePanelCompactField
                    label="Units"
                    value={sidePanelValues.units}
                  />
                  <SidePanelCompactField
                    label="# Of Languages"
                    value={sidePanelValues.numberOfLanguages}
                  />
                  <SidePanelCompactField
                    label="Rate"
                    value={sidePanelValues.rate}
                  />
                  <SidePanelCompactField
                    label="Rate Type"
                    value={sidePanelValues.rateType}
                  />
                </SidePanelFieldGrid>
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              <SidePanelSection
                title="Delivery & Payment"
                icon={<SwapHorizRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelFieldGrid>
                  <SidePanelCompactField
                    label="Delivery Type"
                    value={sidePanelValues.deliveryType}
                  />
                  <SidePanelCompactField
                    label="Payment Type"
                    value={sidePanelValues.paymentType}
                  />
                </SidePanelFieldGrid>
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              <SidePanelSection
                title="Status & Record"
                icon={<TaskAltRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelFieldGrid>
                  <SidePanelCompactField
                    label="Paid"
                    value={sidePanelValues.paid}
                  />
                  <SidePanelCompactField
                    label="Cancelled"
                    value={sidePanelValues.cancelled}
                  />
                  <SidePanelCompactField
                    label="Invoiced"
                    value={sidePanelValues.invoiced}
                  />
                  <SidePanelCompactField
                    label="Checked On"
                    value={sidePanelValues.checkedOn}
                  />
                  <SidePanelCompactField
                    label="Created By"
                    value={sidePanelValues.createdBy}
                  />
                  <SidePanelCompactField
                    label="Checked By"
                    value={sidePanelValues.checkedBy}
                  />
                  <SidePanelGridFullRow>
                    <SidePanelField
                      label="Cancellation Reason"
                      value={sidePanelValues.cancellationReason}
                      multiline
                    />
                  </SidePanelGridFullRow>
                </SidePanelFieldGrid>
              </SidePanelSection>

              <Divider sx={{ my: 0.5 }} />

              <SidePanelSection
                title="Details"
                icon={<NotesRoundedIcon sx={{ fontSize: 16 }} />}
              >
                <SidePanelField
                  label="Details"
                  multiline
                  value={
                    <Typography
                      component="div"
                      sx={{
                        display: "-webkit-box",
                        overflow: "hidden",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: 3,
                        whiteSpace: "pre-wrap",
                        overflowWrap: "anywhere",
                        fontSize: "0.9375rem",
                        fontWeight: 500,
                        color: "text.primary",
                        lineHeight: 1.45,
                      }}
                    >
                      {sidePanelValues.details}
                    </Typography>
                  }
                />
              </SidePanelSection>

              {sidePanelValues.contacts.length > 0 && (
                <>
                  <Divider sx={{ my: 0.5 }} />

                  <SidePanelSection
                    title="Contact Persons"
                    icon={<ContactPhoneRoundedIcon sx={{ fontSize: 16 }} />}
                  >
                    {sidePanelValues.contacts.map((contact) => (
                      <Box
                        key={contact.key}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 1,
                          py: 0.5,
                        }}
                      >
                        <Typography
                          sx={{
                            minWidth: 0,
                            overflowWrap: "anywhere",
                            fontSize: "0.9375rem",
                            fontWeight: 500,
                          }}
                        >
                          {contact.name || "N/A"}
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ flexShrink: 0 }}
                        >
                          {contact.phone !== "N/A" && (
                            <Typography
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.875rem",
                              }}
                            >
                              {contact.phone}
                            </Typography>
                          )}
                          {/* 2026-10-12 (Amine) — per-contact quick actions. */}
                          <SidePanelActions
                            actions={buildDirectContactActions(
                              contact.phone,
                              contact.email,
                            )}
                          />
                        </Stack>
                      </Box>
                    ))}
                  </SidePanelSection>
                </>
              )}

              {sidePanelValues.emailThreadIds.length > 0 && (
                <>
                  <Divider sx={{ my: 0.5 }} />

                  <SidePanelSection
                    title="Email Threads"
                    icon={<EmailRoundedIcon sx={{ fontSize: 16 }} />}
                  >
                    <Stack spacing={0.5} alignItems="flex-start">
                      {sidePanelValues.emailThreadIds.map(
                        (emailThreadId, index) => (
                          <Button
                            key={`${emailThreadId}-${index}`}
                            size="small"
                            variant="outlined"
                            startIcon={
                              <OpenInNewRoundedIcon fontSize="small" />
                            }
                            onClick={() =>
                              window.open(
                                buildEmailThreadLink(emailThreadId),
                                "_blank",
                                "noopener,noreferrer",
                              )
                            }
                          >
                            {sidePanelValues.emailThreadIds.length === 1
                              ? "Open Email Thread"
                              : `Open Email Thread ${index + 1}`}
                          </Button>
                        ),
                      )}
                    </Stack>
                  </SidePanelSection>
                </>
              )}
            </Box>

            <Box
              sx={{
                px: 2,
                py: 1.5,
                flexShrink: 0,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {/* 2026-10-12 (Amine) — footer trio: Open Profile / Edit / ⋮
                  actions. Open Profile keeps the exact profile navigation
                  payload; Edit reuses the grid context menu's edit handler;
                  the ⋮ menu mirrors the row's right-click context menu
                  (same items, icons and handlers) so every profile action
                  is reachable from the panel itself. */}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    flex: 1,
                    minHeight: 44,
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                  }}
                  onClick={() => {
                    // Same navigation payload as onRowDoubleClick above.
                    const data = {
                      Translation: sidePanelRow,
                      TranslationID: sidePanelRow.id,

                      Clients: listOfClients,
                      Linguists: listOfLinguists,
                      Languages: listOfLanguages,
                      Translations: listOfServices,
                      TranslationStatuses: listOfStatuses,
                      TranslationTypes: listOfTypes,
                      ProjectNumber: latestProjectNumber,
                      RateTypes: rateTypes,

                      ComingFrom: `/translations`,
                    };

                    navigate(`/translations/${sidePanelRow.id}`, {
                      state: data,
                    });
                  }}
                >
                  Open Profile
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<EditRoundedIcon />}
                  sx={{ flexShrink: 0, minHeight: 44 }}
                  onClick={handleContextMenuEdit}
                >
                  Edit
                </Button>
                <Tooltip title="More actions">
                  <IconButton
                    aria-label="More actions"
                    onClick={(event) =>
                      setPanelMenuAnchor(event.currentTarget)
                    }
                    sx={{
                      flexShrink: 0,
                      width: 44,
                      height: 44,
                      borderRadius: "12px",
                      color: theme.palette.primary.main,
                      border: `1px solid ${theme.palette.primary.border}`,
                      "&:hover": {
                        backgroundColor: theme.palette.primary.hover,
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <MoreVertRoundedIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {/* Footer "More actions" menu — 2026-10-13 (Amine): mirrors the
                Assignment Profile's right-column Actions panel (same
                sections, labels, icons, gates, dialogs and payloads), NOT
                the grid's right-click context menu. Every item also closes
                this menu. */}
            <Menu
              anchorEl={panelMenuAnchor}
              open={Boolean(panelMenuAnchor)}
              onClose={() => setPanelMenuAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "bottom", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    bgcolor: theme.palette.background.alt,
                    border: `1px solid ${theme.palette.divider}`,
                    "& .MuiListItemIcon-root": {
                      color: theme.palette.primary.main,
                    },
                  },
                },
              }}
            >
              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  lineHeight: 2,
                }}
              >
                Assignment
              </ListSubheader>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handleContextMenuEdit();
                }}
              >
                <ListItemIcon>
                  <EditRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit Assignment</ListItemText>
              </MenuItem>

              {canCompletePanelSelected && (
                <MenuItem
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handleContextMenuComplete();
                  }}
                >
                  <ListItemIcon>
                    <TaskRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Complete Assignment</ListItemText>
                </MenuItem>
              )}

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handlePanelMarkAsChecked();
                }}
              >
                <ListItemIcon>
                  <VerifiedUserRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Mark As Checked</ListItemText>
              </MenuItem>

              {canMarkPanelSelectedAsPaid ? (
                <MenuItem
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handlePanelMarkAsPaid();
                  }}
                >
                  <ListItemIcon>
                    <CreditScoreRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Mark As Paid</ListItemText>
                </MenuItem>
              ) : canMarkPanelSelectedAsUnpaid ? (
                <MenuItem
                  onClick={() => {
                    setPanelMenuAnchor(null);
                    handlePanelMarkAsUnpaid();
                  }}
                >
                  <ListItemIcon>
                    <RestartAltRoundedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Mark As Unpaid</ListItemText>
                </MenuItem>
              ) : null}

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handlePanelChangeCategory();
                }}
              >
                <ListItemIcon>
                  <CategoryRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Category</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handlePanelDeleteAssignment();
                }}
              >
                <ListItemIcon>
                  <DeleteRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete Assignment</ListItemText>
              </MenuItem>

              <Divider sx={{ my: 0.5 }} />

              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  lineHeight: 2,
                }}
              >
                Tasks & Notes
              </ListSubheader>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handleContextMenuAddTask();
                }}
              >
                <ListItemIcon>
                  <AddTaskRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Task</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handleContextMenuAddNote();
                }}
              >
                <ListItemIcon>
                  <CommentRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Note</ListItemText>
              </MenuItem>

              <Divider sx={{ my: 0.5 }} />

              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  lineHeight: 2,
                }}
              >
                Utilities
              </ListSubheader>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);
                  handlePanelCopyTrackerRow();
                }}
              >
                <ListItemIcon>
                  <ContentCopyRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy Tracker Row</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setPanelMenuAnchor(null);

                  const year = dayjs(selectedTranslation?.Date).format(
                    "YYYY",
                  );

                  const oneDriveLink =
                    "https://communicationessentialsllc-my.sharepoint.com/personal/amer_communicationessentialsllc_com/_layouts/15/onedrive.aspx?id=%2Fpersonal%2Famer%5Fcommunicationessentialsllc%5Fcom%2FDocuments%2FCommunication%20Essentials%2FAssignments%2FTranslations%20" +
                    year +
                    "%2F" +
                    selectedTranslation?.ProjectNumber;

                  window.open(oneDriveLink, "_blank");
                }}
              >
                <ListItemIcon>
                  <CloudIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Open One Drive Link</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Box>

      {/* Context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuEdit}
        >
          <ListItemIcon>
            <EditRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Edit
          </ListItemText>
        </MenuItem>

        {canCompleteSelected && (
          <MenuItem
            onMouseEnter={handleStatusMenuClose}
            onClick={handleContextMenuComplete}
          >
            <ListItemIcon>
              <TaskRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Complete
            </ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onMouseEnter={handleStatusMenuOpen}
          onMouseLeave={handleStatusMenuLeave}
          onClick={(event) => event.stopPropagation()}
        >
          <ListItemIcon>
            <SwapHorizRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Status
          </ListItemText>
          <ChevronRightRoundedIcon fontSize="small" />
        </MenuItem>

        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuDuplicate}
        >
          <ListItemIcon>
            <ContentCopyRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Duplicate
          </ListItemText>
        </MenuItem>

        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuAddTask}
        >
          <ListItemIcon>
            <AddTaskRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Add Task
          </ListItemText>
        </MenuItem>

        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuAddNote}
        >
          <ListItemIcon>
            <CommentRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Add Note
          </ListItemText>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuCallRequestor}
        >
          <ListItemIcon>
            <CallRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Call{" "}
            {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
              ?.Name || "Requestor"}
          </ListItemText>
        </MenuItem>

        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuEmailRequestor}
        >
          <ListItemIcon>
            <EmailRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Email{" "}
            {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
              ?.Name || "Requestor"}
          </ListItemText>
        </MenuItem>
      </Menu>

      <Popper
        open={Boolean(statusMenuAnchor)}
        anchorEl={statusMenuAnchor}
        placement="right-start"
        sx={{ zIndex: theme.zIndex.modal + 1 }}
      >
        <Paper
          elevation={8}
          onMouseEnter={handleStatusMenuEnter}
          onMouseLeave={handleStatusMenuLeave}
          sx={{
            minWidth: 150,
            py: 1,
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "12px",
            "& .MuiListItemIcon-root": {
              color: theme.palette.primary.main,
            },
          }}
        >
          {statusOptions.map((status) => (
            <MenuItem
              key={status}
              onClick={() => handleContextMenuStatusChange(status)}
            >
              <ListItemIcon>
                {status === currentStatus ? (
                  <CheckRoundedIcon fontSize="small" />
                ) : (
                  <span style={{ width: 20 }} />
                )}
              </ListItemIcon>
              <ListItemText sx={{ color: theme.palette.primary.text }}>
                {status}
              </ListItemText>
            </MenuItem>
          ))}
        </Paper>
      </Popper>

      {overlayOpen && overlayMode && (
        <Popover
          open={overlayOpen}
          anchorReference="anchorPosition"
          anchorPosition={{
            left: window.innerWidth / 2,
            top: window.innerHeight / 2,
          }}
          onClose={() => setOverlayOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              borderRadius: "12px",
              maxWidth: "calc(100vw - 2rem)",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.5)"
                  : "0 8px 32px rgba(0,0,0,0.15)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}
    </Box>
  );
};

export default Translations;
